/**
 * =========================================================================================
 * SHERGOLD PRIME ERP - INVOICE & RECEIPT DESIGNER STUDIO
 * =========================================================================================
 *
 * COMPONENT OVERVIEW:
 * An interactive drag-and-drop template designer studio for generating customizable PDF
 * receipts, tax invoices, gold passbook vouchers, and barcode tags.
 *
 * KEY FEATURES:
 * 1. Multi-Paper Support: Preset dimensions for 57mm Thermal, 80mm POS, A4, A5, and custom tags.
 * 2. Visual Element Schema Editor: Interactive canvas powered by @pdfme/ui for positioning text, images, tables, and barcodes.
 * 3. Dynamic Token Integration: Dynamic field variables (e.g., {{CustomerName}}, {{TotalAmount}}) evaluated at print time.
 * 4. Live PDF Preview: Real-time PDF compilation rendering with mock data via @pdfme/generator.
 * 5. Database Persistence: Loads and saves template definitions to the ERP SQL database.
 * =========================================================================================
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import Swal from "sweetalert2";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import "./InvoiceDesigner.css";
import PrintModelAssigner from "../PrintModelAssigner/PrintModelAssigner";
import InvoiceDesignerManualModal from "./InvoiceDesignerManualModal";
import { clearPrintTemplateCache, notifyPrintTemplateUpdated } from "../../../hooks/usePrintService";

// PDFME Core Engine Modules
import { Designer } from "@pdfme/ui";         // Interactive canvas component
import { generate } from "@pdfme/generator";  // PDF rendering and compilation engine
import { getDefaultFont } from "@pdfme/common";
import {
  text,               // Text element schema with token support
  image,              // Image schema for logos and graphics
  line,               // Divider line schema
  rectangle,          // Border and background box schema
  table,              // Data table schema for itemized lists
  multiVariableText,  // Formatted multi-line text schema
  barcodes            // Barcode element schemas (QR Code, Code128, EAN13)
} from "@pdfme/schemas";

// System Tokens & Sample Presets
import { TOKEN_CATEGORIES, SAMPLE_DATA_MAP, resolveTemplateInputs } from "./systemTokens";
import { SAMPLE_TEMPLATES } from "./sampleTemplates";
import { DEFAULT_PRESET_LOGO } from "./defaultPresetLogo.js";
import { SYMBOL_CATEGORIES, POPULAR_QUICK_SYMBOLS } from "./printSymbols";

// Icons
import {
  FiFileText,
  FiLayers,
  FiRefreshCw,
  FiEye,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiType,
  FiCode,
  FiGrid,
  FiImage,
  FiMinus,
  FiDownload,
  FiPlus,
  FiMaximize,
  FiCheckCircle,
  FiInfo,
  FiSliders,
  FiUser,
  FiAward,
  FiCreditCard,
  FiHome,
  FiX,
  FiTrash2,
  FiTag,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiArrowUp,
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiMove,
  FiColumns,
  FiDivide,
  FiMinimize2,
  FiMaximize2,
  FiTable,
  FiLayout,
  FiChevronRight,
  FiPrinter,
  FiHelpCircle
} from "react-icons/fi";

const INTER = { fontFamily: "Inter, sans-serif" };

// Category Icons Mapping
const CATEGORY_ICONS = {
  sales: FiTag,
  purchase: FiRefreshCw,
  customer: FiUser,
  scheme: FiAward,
  financials: FiCreditCard,
  company: FiHome
};

/**
 * PAPER_SIZES:
 * Pre-configured print paper dimensions commonly used in jewelry ERP applications.
 */
const PAPER_SIZES = [
  { id: "58MM", name: "58mm Thermal Roll (58 × 130 mm)", width: 58, height: 130, padding: [2, 2, 2, 2] },
  { id: "80MM", name: "80mm POS Thermal (80 × 160 mm)", width: 80, height: 160, padding: [3, 3, 3, 3] },
  { id: "A4", name: "Standard A4 Sheet (210 × 297 mm)", width: 210, height: 297, padding: [10, 10, 10, 10] },
  { id: "A5", name: "A5 Sheet Portrait (148 × 210 mm)", width: 148, height: 210, padding: [8, 8, 8, 8] },
  { id: "A5_LANDSCAPE", name: "A5 Landscape / Half-A4 (210 × 148 mm)", width: 210, height: 148, padding: [8, 8, 8, 8] },
  { id: "RAT_TAIL", name: "Rat-Tail Barcode Tag (100 × 12 mm)", width: 100, height: 12, padding: [0.5, 0.5, 0.5, 0.5] },
  { id: "RFID_TAG", name: "RFID Jewelry Tag (40 × 25 mm)", width: 40, height: 25, padding: [1, 1, 1, 1] },
  { id: "CUSTOM", name: "Custom Dimensions...", width: 58, height: 130, padding: [2, 2, 2, 2] }
];

/**
 * STANDARD_VOUCHER_TYPES:
 * Standard ERP voucher classification suggestions for template categorization.
 */
const STANDARD_VOUCHER_TYPES = [
  "SALE INVOICE",
  "PURCHASE VOUCHER",
  "RECEIPT",
  "PAYMENT",
  "SCHEME RECEIPT",
  "ESTIMATE",
  "OLD GOLD",
  "CASH RECEIPT",
  "CASH PAYMENT",
  "BARCODE TAG",
  "RFID TAG",
  "CUSTOMER PASSBOOK",
  "CONTRA VOUCHER",
  "JOURNAL VOUCHER"
];

/**
 * VOUCHER_METADATA:
 * Categorization, human-readable labels, hints, and badge styles for the Print Model Assigner Hub.
 */
const VOUCHER_METADATA = {
  "SALE INVOICE": { label: "Sale Invoice", category: "sales", hint: "Showroom retail jewellery sales tax bill (A4 / Thermal)", icon: FiTag, badgeColor: "bg-blue-100 text-blue-800 border-blue-200" },
  "RECEIPT": { label: "Receipt Voucher", category: "vouchers", hint: "Cash / bank collection receipt voucher (A4 / A5)", icon: FiCreditCard, badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "PAYMENT": { label: "Payment Voucher", category: "vouchers", hint: "Cash / bank disbursement payment voucher (A4 / A5)", icon: FiCreditCard, badgeColor: "bg-rose-100 text-rose-800 border-rose-200" },
  "ESTIMATE": { label: "Sales Estimate", category: "sales", hint: "Quotation & preliminary gold ornament estimate bill", icon: FiFileText, badgeColor: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  "PURCHASE VOUCHER": { label: "Purchase Voucher", category: "purchase", hint: "Bullion acquisition & supplier stock inward voucher", icon: FiRefreshCw, badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "OLD GOLD": { label: "Old Gold Assessment", category: "purchase", hint: "Scrap gold valuation, melting touch loss & buyback receipt", icon: FiRefreshCw, badgeColor: "bg-teal-100 text-teal-800 border-teal-200" },
  "SCHEME RECEIPT": { label: "Scheme Receipt", category: "scheme", hint: "Customer monthly gold savings scheme installment voucher", icon: FiAward, badgeColor: "bg-amber-100 text-amber-800 border-amber-200" },
  "CASH RECEIPT": { label: "Cash Receipt", category: "scheme", hint: "Counter cash collection receipt voucher", icon: FiCreditCard, badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "CASH PAYMENT": { label: "Cash Payment", category: "scheme", hint: "Counter cash disbursement payment slip", icon: FiCreditCard, badgeColor: "bg-rose-100 text-rose-800 border-rose-200" },
  "BARCODE TAG": { label: "Barcode Tag", category: "tags", hint: "Jewellery rat-tail barcode sticker for ornaments & boxes", icon: FiCode, badgeColor: "bg-purple-100 text-purple-800 border-purple-200" },
  "RFID TAG": { label: "RFID Smart Tag", category: "tags", hint: "Contactless RFID jewellery barbell tag with inlay chip", icon: FiCode, badgeColor: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200" },
  "CUSTOMER PASSBOOK": { label: "Gold Passbook", category: "accounts", hint: "Gold scheme customer passbook cover & transaction page", icon: FiUser, badgeColor: "bg-cyan-100 text-cyan-800 border-cyan-200" },
  "CONTRA VOUCHER": { label: "Contra Voucher", category: "accounts", hint: "Cash deposit, withdrawal & inter-bank fund transfers", icon: FiLayers, badgeColor: "bg-slate-100 text-slate-800 border-slate-200" },
  "JOURNAL VOUCHER": { label: "Journal Voucher", category: "accounts", hint: "Double-entry adjustment and general journal vouchers", icon: FiSliders, badgeColor: "bg-slate-100 text-slate-800 border-slate-200" }
};

/**
 * PRESET_GALLERY_ITEMS:
 * Rich visual metadata for the 10 pre-configured jewellery print presets.
 * Modeled after the luxury card template design pattern in AppConfigMaster.jsx.
 */
const PRESET_GALLERY_ITEMS = [
  {
    id: "a4Sales",
    templateKey: "a4Sales",
    name: "A4 Luxury Jewellery Tax Invoice",
    subtitle: "Showroom Flagship Tax Bill with HUID Badge",
    category: "sales",
    categoryLabel: "Sales Billing",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "SALE INVOICE",
    cardBg: "linear-gradient(145deg, #0f2d59 0%, #17386d 60%, #0a1f3d 100%)",
    ringColor: "border-amber-400/25",
    themeBadge: "bg-sky-500/20 text-sky-200 border-sky-400/30",
    columnsCount: "10 Columns",
    highlights: ["BIS Hallmark & HUID", "Old Gold Deduction", "UPI Dynamic QR", "Terms & Bank Details"],
    description: "Full regulatory compliance tax invoice with 10-column table, HUID laser badge, exchange gold deduction, CGST/SGST 3%, and instant UPI QR."
  },
  {
    id: "a4Purchase",
    templateKey: "a4Purchase",
    name: "A4 Old Gold Assaying & Purchase Voucher",
    subtitle: "Official Reverse-Charge Assaying & Melting Slip",
    category: "purchase",
    categoryLabel: "Purchase & Buyback",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "PURCHASE VOUCHER",
    cardBg: "linear-gradient(145deg, #064e3b 0%, #0a664d 60%, #022c22 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "9 Columns",
    highlights: ["Touch Assaying %", "Melting Loss Deductions", "Sec 9(4) GST Legal Declaration", "Thumb Impression Box"],
    description: "Official jewellery purchase and old gold intake voucher with melting loss calculations, touch assaying %, ID verification, and statutory legal declaration."
  },
  {
    id: "a5Sales",
    templateKey: "a5Sales",
    name: "A5 Executive Jewellery Sales Invoice",
    subtitle: "High-Density Compact Showroom Counter Bill",
    category: "sales",
    categoryLabel: "Sales Billing",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "SALE INVOICE",
    cardBg: "linear-gradient(145deg, #1e293b 0%, #334155 60%, #0f172a 100%)",
    ringColor: "border-amber-400/25",
    themeBadge: "bg-sky-500/20 text-sky-200 border-sky-400/30",
    columnsCount: "7 Columns",
    highlights: ["Compact Density", "HUID Laser ID", "UPI QR Code", "Gross/Net Grams"],
    description: "Elegant half-page A5 tax invoice designed for quick retail counters, saving paper while retaining essential HUID, hallmark, and GST breakdowns."
  },
  {
    id: "a5Purchase",
    templateKey: "a5Purchase",
    name: "A5 Old Gold Purchase & Exchange Slip",
    subtitle: "Counter Buyback & Scrap Gold Exchange Slip",
    category: "purchase",
    categoryLabel: "Purchase & Buyback",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "PURCHASE VOUCHER",
    cardBg: "linear-gradient(145deg, #064e3b 0%, #047857 60%, #022c22 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "7 Columns",
    highlights: ["Touch Assaying", "Net Pure Gold", "Cash/Bank Payout", "Customer Signature"],
    description: "Streamlined A5 old gold acquisition and exchange slip showing gross weight, melting loss, touch purity, and final cash/bank payout."
  },
  {
    id: "thermal80mmSale",
    templateKey: "thermal80mmSale",
    name: "80mm POS Jewellery Sales Bill",
    subtitle: "Modern 80mm Thermal Receipt with Item Table",
    category: "sales",
    categoryLabel: "Sales Billing",
    paperSize: "80MM",
    dimensions: "80 × 160 mm",
    voucherType: "SALE INVOICE",
    cardBg: "linear-gradient(145deg, #1e293b 0%, #1e3a8a 60%, #0a192f 100%)",
    ringColor: "border-cyan-400/25",
    themeBadge: "bg-cyan-500/20 text-cyan-200 border-cyan-400/30",
    columnsCount: "5 Columns",
    highlights: ["5-Col Item Table", "UPI Dynamic QR", "Code128 Barcode", "GST Breakdown"],
    description: "High-speed 80mm thermal receipt with itemized jewellery weights, stone deductions, GST 3% calculation, barcode, and instant UPI QR code."
  },
  {
    id: "thermal80mmPurchase",
    templateKey: "thermal80mmPurchase",
    name: "80mm Old Gold Buying Slip",
    subtitle: "Counter Old Gold Thermal Buyback Receipt",
    category: "purchase",
    categoryLabel: "Purchase & Buyback",
    paperSize: "80MM",
    dimensions: "80 × 160 mm",
    voucherType: "PURCHASE VOUCHER",
    cardBg: "linear-gradient(145deg, #064e3b 0%, #115e59 60%, #022c22 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "Touch Assaying",
    highlights: ["Touch Assaying %", "Net Pure Gold", "Payout Summary", "Quick Sign"],
    description: "Fast 80mm thermal buying slip for quick gold scrap and old ornament intake with touch purity calculations and payout summary."
  },
  {
    id: "thermal58mmSale",
    templateKey: "thermal58mmSale",
    name: "58mm Compact Jewellery Sales Bill",
    subtitle: "Ultra-Compact Thermal Bill for Mobile POS",
    category: "sales",
    categoryLabel: "Sales Billing",
    paperSize: "58MM",
    dimensions: "58 × 130 mm",
    voucherType: "SALE INVOICE",
    cardBg: "linear-gradient(145deg, #1e293b 0%, #475569 60%, #0f172a 100%)",
    ringColor: "border-slate-400/25",
    themeBadge: "bg-slate-500/20 text-slate-200 border-slate-400/30",
    columnsCount: "Compact 22K",
    highlights: ["22K 916 Hallmark", "GST Totals", "Mini UPI QR", "Handheld POS"],
    description: "Streamlined 58mm thermal bill for handheld POS terminals, exhibitions, and mobile sales counters with GST summary."
  },
  {
    id: "thermal58mmPurchase",
    templateKey: "thermal58mmPurchase",
    name: "58mm Old Gold Buyback Slip",
    subtitle: "Instant Cash Scrap Purchase Voucher",
    category: "purchase",
    categoryLabel: "Purchase & Buyback",
    paperSize: "58MM",
    dimensions: "58 × 130 mm",
    voucherType: "PURCHASE VOUCHER",
    cardBg: "linear-gradient(145deg, #064e3b 0%, #0f766e 60%, #022c22 100%)",
    ringColor: "border-teal-400/25",
    themeBadge: "bg-teal-500/20 text-teal-200 border-teal-400/30",
    columnsCount: "Quick Buyback",
    highlights: ["Instant Cash", "Weight & Touch", "Quick Sign", "Minimalist"],
    description: "Lightweight 58mm counter slip for instant cash purchase and scrap buyback with weight and payout fields."
  },
  {
    id: "ratTailTag",
    templateKey: "ratTailTag",
    name: "Rat-Tail Jewellery Barcode Tag",
    subtitle: "100×12 mm Dumbbell Label for Rings & Chains",
    category: "tags",
    categoryLabel: "Tags & Barcodes",
    paperSize: "RAT_TAIL",
    dimensions: "100 × 12 mm",
    voucherType: "BARCODE TAG",
    cardBg: "linear-gradient(145deg, #3b0764 0%, #581c87 60%, #1e1b4b 100%)",
    ringColor: "border-purple-400/25",
    themeBadge: "bg-purple-500/20 text-purple-200 border-purple-400/30",
    columnsCount: "Dumbbell Tail",
    highlights: ["Gross & Net Wt", "HUID Laser ID", "Code128 Barcode", "MRP & Purity"],
    description: "Standard jewellery dumbbell rat-tail tag with left barbell for weights and HUID, center tail loop, and right barbell with barcode."
  },
  {
    id: "rfidTag",
    templateKey: "rfidTag",
    name: "RFID Smart Jewellery Tag",
    subtitle: "40×25 mm High-Security Smart Tag",
    category: "tags",
    categoryLabel: "Tags & Barcodes",
    paperSize: "RFID_TAG",
    dimensions: "40 × 25 mm",
    voucherType: "RFID TAG",
    cardBg: "linear-gradient(145deg, #4a044e 0%, #701a75 60%, #1e1b4b 100%)",
    ringColor: "border-fuchsia-400/25",
    themeBadge: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/30",
    columnsCount: "Smart Tag",
    highlights: ["EPC / RFID Chip", "QR + Barcode", "Gross & Net Wt", "Tray Label"],
    description: "High-density smart jewellery tag for showcase trays and RFID stock audits with scannable QR, barcode, and chip tracking."
  },
  {
    id: "a4Receipt",
    templateKey: "a4Receipt",
    name: "A4 Classic Cash / Bank Receipt",
    subtitle: "Full A4 Clean Shersoft Voucher with Currency Badge",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #18181b 0%, #27272a 60%, #09090b 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "Single Page A4",
    highlights: ["Black Currency Box", "Gold Rate & Weight", "Account & Party", "Outer Border"],
    description: "Standard Shersoft A4 receipt voucher with top centered branding, Account No, Received From, Amount In Words, solid black ₹ box, gold weights, and Receiver Signature."
  },
  {
    id: "a5Receipt",
    templateKey: "a5Receipt",
    name: "A5 Compact Cash / Bank Receipt",
    subtitle: "A5 Portrait Clean Shersoft Receipt Voucher",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #1e293b 0%, #334155 60%, #0f172a 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "A5 Portrait",
    highlights: ["A5 Portrait", "Currency ₹ Box", "Rate / Gm & Weights", "Receiver Signature"],
    description: "Compact A5 portrait receipt voucher layout matching standard Shersoft ERP receipts with account number, customer name, payment mode, and bullion rate/weights."
  },
  {
    id: "a5LandscapeReceipt",
    templateKey: "a5LandscapeReceipt",
    name: "A5 Landscape / Half-A4 Receipt",
    subtitle: "Half-A4 Sheet Classic Dot-Matrix / Laser Voucher",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5_LANDSCAPE",
    dimensions: "210 × 148 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #022c22 0%, #064e3b 60%, #022c22 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "Half-A4 (210×148)",
    highlights: ["Landscape Half-A4", "₹ Amount Box", "Rate/Gm & Grams", "Signature"],
    description: "Exact replica of the classic Shersoft Half-A4 landscape printout for continuous stationary and halved A4 sheets with currency box and weight fields."
  },
  {
    id: "a4Payment",
    templateKey: "a4Payment",
    name: "A4 Classic Cash / Bank Payment",
    subtitle: "Full A4 Disbursement Voucher with Currency Badge",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "PAYMENT",
    cardBg: "linear-gradient(145deg, #450a0a 0%, #7f1d1d 60%, #200404 100%)",
    ringColor: "border-rose-400/25",
    themeBadge: "bg-rose-500/20 text-rose-200 border-rose-400/30",
    columnsCount: "Single Page A4",
    highlights: ["Black Currency Box", "Paid To & Account", "Gold Rate & Weight", "Outer Border"],
    description: "Full-page A4 payment voucher with Paid To, Account No, Amount In Words, Payment Mode, solid black ₹ box, gold weights, and Receiver Signature."
  },
  {
    id: "a5Payment",
    templateKey: "a5Payment",
    name: "A5 Compact Cash / Bank Payment",
    subtitle: "A5 Portrait Clean Shersoft Payment Voucher",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "PAYMENT",
    cardBg: "linear-gradient(145deg, #3f1010 0%, #581c1c 60%, #1e0505 100%)",
    ringColor: "border-rose-400/25",
    themeBadge: "bg-rose-500/20 text-rose-200 border-rose-400/30",
    columnsCount: "A5 Portrait",
    highlights: ["A5 Portrait", "Currency ₹ Box", "Rate / Gm & Weights", "Authorized Sign"],
    description: "Compact A5 portrait payment voucher layout matching standard Shersoft ERP cash disbursement slips with Paid To, account code, and weight calculations."
  },
  {
    id: "a5LandscapePayment",
    templateKey: "a5LandscapePayment",
    name: "A5 Landscape / Half-A4 Payment",
    subtitle: "Half-A4 Sheet Classic Dot-Matrix / Laser Voucher",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5_LANDSCAPE",
    dimensions: "210 × 148 mm",
    voucherType: "PAYMENT",
    cardBg: "linear-gradient(145deg, #4c0519 0%, #881337 60%, #29020d 100%)",
    ringColor: "border-rose-400/25",
    themeBadge: "bg-rose-500/20 text-rose-200 border-rose-400/30",
    columnsCount: "Half-A4 (210×148)",
    highlights: ["Landscape Half-A4", "₹ Amount Box", "Rate/Gm & Grams", "Signature"],
    description: "Exact replica of the classic Shersoft Half-A4 landscape payment voucher for continuous stationary and halved A4 sheets with currency box and weight fields."
  },
  {
    id: "thermal80mmReceipt",
    templateKey: "thermal80mmReceipt",
    name: "80mm POS Thermal Receipt Voucher",
    subtitle: "80mm Continuous Roll Cash / Bank Receipt",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "80MM",
    dimensions: "80 × 150 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #064e3b 0%, #0f766e 60%, #042f2e 100%)",
    ringColor: "border-teal-400/25",
    themeBadge: "bg-teal-500/20 text-teal-200 border-teal-400/30",
    columnsCount: "80mm Roll",
    highlights: ["80mm POS Roll", "Currency ₹ Box", "Rate/Gm & Weights", "Receiver Signature"],
    description: "High-speed 80mm thermal receipt voucher for POS counter printers with centered title, account code, customer name, ₹ currency box, and gold rates."
  },
  {
    id: "thermal58mmReceipt",
    templateKey: "thermal58mmReceipt",
    name: "58mm POS Thermal Receipt Voucher",
    subtitle: "58mm Compact Handheld Roll Receipt Slip",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "58MM",
    dimensions: "58 × 130 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #134e4a 0%, #115e59 60%, #042f2e 100%)",
    ringColor: "border-emerald-400/25",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "58mm Roll",
    highlights: ["58mm Compact Roll", "₹ Currency Box", "Weight & Rate", "Minimalist"],
    description: "Ultra-compact 58mm POS thermal receipt slip tailored for mobile POS terminals, handheld Bluetooth printers, and compact cash drawers."
  },
  {
    id: "thermal58mmReceiptSmart",
    templateKey: "thermal58mmReceiptSmart",
    name: "58mm Smart Receipt (Logo & UPI QR)",
    subtitle: "58mm Compact Roll with Store Logo, UPI QR & Website",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "58MM",
    dimensions: "58 × 165 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #0f766e 0%, #115e59 60%, #042f2e 100%)",
    ringColor: "border-emerald-400/40",
    themeBadge: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
    columnsCount: "58mm Roll",
    highlights: ["Store Logo", "UPI QR Code", "Web Address", "₹ Amount Box"],
    description: "Compact 58mm POS thermal receipt voucher enhanced with store logo image, scannable UPI QR code, official website, and gold bullion weights."
  },
  {
    id: "thermal80mmReceiptSmart",
    templateKey: "thermal80mmReceiptSmart",
    name: "80mm Smart Receipt (Logo & UPI QR)",
    subtitle: "80mm Continuous Roll with Store Logo, UPI QR & Website",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "80MM",
    dimensions: "80 × 175 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #065f46 0%, #047857 60%, #064e3b 100%)",
    ringColor: "border-teal-400/40",
    themeBadge: "bg-teal-500/20 text-teal-200 border-teal-400/30",
    columnsCount: "80mm Roll",
    highlights: ["Store Logo", "UPI QR Code", "Web Address", "₹ Amount Box"],
    description: "High-speed 80mm thermal receipt voucher for POS counter printers with store branding logo, instant UPI QR payment, website URL, and gold rates."
  },
  {
    id: "a5ReceiptSmart",
    templateKey: "a5ReceiptSmart",
    name: "A5 Smart Receipt Voucher (Logo & UPI QR)",
    subtitle: "A5 Portrait Passbook with Store Logo, UPI QR & Website",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #1e3a5f 0%, #0f2d59 60%, #091e3a 100%)",
    ringColor: "border-sky-400/40",
    themeBadge: "bg-sky-500/20 text-sky-200 border-sky-400/30",
    columnsCount: "A5 Portrait",
    highlights: ["Store Logo", "UPI QR Code", "Web Address", "Passbook Ready"],
    description: "Executive A5 portrait receipt voucher featuring showroom brand logo, payment UPI QR code, website address, ₹ currency box, and bullion weight breakdown."
  },
  {
    id: "a4ReceiptSmart",
    templateKey: "a4ReceiptSmart",
    name: "A4 Smart Receipt Voucher (Logo & UPI QR)",
    subtitle: "Full A4 Standard Sheet with Store Logo, UPI QR & Website",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #0f172a 0%, #1e293b 60%, #020617 100%)",
    ringColor: "border-indigo-400/40",
    themeBadge: "bg-indigo-500/20 text-indigo-200 border-indigo-400/30",
    columnsCount: "Single Page A4",
    highlights: ["Store Logo", "UPI QR Code", "Web Address", "Executive Layout"],
    description: "Full-page A4 receipt voucher designed with prominent store brand logo, dynamic UPI QR payment code, showroom website address, and gold ledger details."
  },
  {
    id: "thermal58mmReceiptModern",
    templateKey: "thermal58mmReceiptModern",
    name: "58mm Modern Executive Receipt",
    subtitle: "58mm Compact Roll with Hero Banner, Bullion Card & UPI QR",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "58MM",
    dimensions: "58 × 165 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #022c22 0%, #064e3b 60%, #0f172a 100%)",
    ringColor: "border-emerald-400/40",
    themeBadge: "bg-emerald-500/25 text-emerald-200 border-emerald-400/35",
    columnsCount: "58mm Roll",
    highlights: ["Hero ₹ Banner", "Bullion Ledger", "Store Logo", "Instant UPI QR"],
    description: "Modern executive 58mm POS receipt layout featuring asymmetric high-contrast branding, hero currency banner, bullion gold accumulation card, and scannable UPI QR."
  },
  {
    id: "thermal80mmReceiptModern",
    templateKey: "thermal80mmReceiptModern",
    name: "80mm Modern Executive Receipt",
    subtitle: "80mm Continuous Roll with Hero Banner, Bullion Card & UPI QR",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "80MM",
    dimensions: "80 × 175 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #042f2e 0%, #0d9488 60%, #0f172a 100%)",
    ringColor: "border-teal-400/40",
    themeBadge: "bg-teal-500/25 text-teal-200 border-teal-400/35",
    columnsCount: "80mm Roll",
    highlights: ["Hero ₹ Banner", "Bullion Ledger", "Store Logo", "Instant UPI QR"],
    description: "High-speed 80mm modern executive thermal voucher with bold navy hero amount banner, 3-point gold scheme ledger statement, and crisp QR code."
  },
  {
    id: "a5ReceiptModern",
    templateKey: "a5ReceiptModern",
    name: "A5 Modern Executive Receipt Voucher",
    subtitle: "A5 Portrait Passbook with Split Header, Hero Banner & Gold Statement",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A5",
    dimensions: "148 × 210 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #0f2d59 0%, #1e40af 60%, #020617 100%)",
    ringColor: "border-blue-400/40",
    themeBadge: "bg-blue-500/25 text-blue-200 border-blue-400/35",
    columnsCount: "A5 Portrait",
    highlights: ["Split Executive Header", "Hero ₹ Banner", "3-Col Gold Passbook", "Dual Footer QR"],
    description: "Premium executive A5 portrait receipt voucher designed with dual-column split brand header, high-impact total amount hero bar, 3-column gold accumulation card, and side-by-side cashier signature."
  },
  {
    id: "a4ReceiptModern",
    templateKey: "a4ReceiptModern",
    name: "A4 Modern Executive Receipt Voucher",
    subtitle: "Full A4 Luxury Sheet with Split Header, Hero Banner & Gold Statement",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "A4",
    dimensions: "210 × 297 mm",
    voucherType: "RECEIPT",
    cardBg: "linear-gradient(145deg, #0f172a 0%, #1e3a8a 60%, #020617 100%)",
    ringColor: "border-indigo-400/40",
    themeBadge: "bg-indigo-500/25 text-indigo-200 border-indigo-400/35",
    columnsCount: "Single Page A4",
    highlights: ["Split Executive Header", "Hero ₹ Banner", "3-Col Gold Passbook", "Dual Footer QR"],
    description: "Full-page flagship A4 modern executive jewellery receipt voucher featuring elegant split branding, official audit card, solid navy hero amount banner, bullion savings ledger, and dedicated UPI QR payment box."
  },
  {
    id: "thermal80mmPayment",
    templateKey: "thermal80mmPayment",
    name: "80mm POS Thermal Payment Voucher",
    subtitle: "80mm Continuous Roll Cash / Bank Payment",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "80MM",
    dimensions: "80 × 150 mm",
    voucherType: "PAYMENT",
    cardBg: "linear-gradient(145deg, #881337 0%, #9f1239 60%, #4c0519 100%)",
    ringColor: "border-rose-400/25",
    themeBadge: "bg-rose-500/20 text-rose-200 border-rose-400/30",
    columnsCount: "80mm Roll",
    highlights: ["80mm POS Roll", "Currency ₹ Box", "Paid To & Rate", "Authorized Sign"],
    description: "High-speed 80mm thermal disbursement slip for POS counter printers with centered title, account code, payee name, ₹ currency box, and gold rates."
  },
  {
    id: "thermal58mmPayment",
    templateKey: "thermal58mmPayment",
    name: "58mm POS Thermal Payment Voucher",
    subtitle: "58mm Compact Handheld Roll Payment Slip",
    category: "vouchers",
    categoryLabel: "Receipt & Payment",
    paperSize: "58MM",
    dimensions: "58 × 130 mm",
    voucherType: "PAYMENT",
    cardBg: "linear-gradient(145deg, #701a75 0%, #831843 60%, #3b0764 100%)",
    ringColor: "border-fuchsia-400/25",
    themeBadge: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/30",
    columnsCount: "58mm Roll",
    highlights: ["58mm Compact Roll", "₹ Currency Box", "Weight & Rate", "Minimalist"],
    description: "Ultra-compact 58mm POS thermal payment slip tailored for mobile POS terminals, handheld Bluetooth printers, and cash disbursement."
  }
];

/**
 * PresetCardThumbnail:
 * Renders an authentic miniature silhouette of the target paper format (A4, A5, Thermal, Dumbbell tag).
 */
function PresetCardThumbnail({ item }) {
  if (item.id.includes("Modern")) {
    const isThermal = item.id.startsWith("thermal");
    const is80mm = item.id.includes("80mm");
    const is58mm = item.id.includes("58mm");
    const isA5 = item.id.startsWith("a5");

    const widthClass = is58mm
      ? "w-[34px] h-[64px]"
      : is80mm
      ? "w-[42px] h-[66px]"
      : isA5
      ? "w-[44px] h-[62px]"
      : "w-[48px] h-[66px]";

    return (
      <div
        className={`${widthClass} bg-white rounded shadow-xs ${isThermal ? 'border-x border-t border-slate-900' : 'border border-blue-900'} p-0.5 flex flex-col justify-between shrink-0 font-sans relative`}
        style={
          isThermal
            ? {
                clipPath:
                  "polygon(0% 0%, 100% 0%, 100% 95%, 88% 100%, 75% 95%, 62% 100%, 50% 95%, 38% 100%, 25% 95%, 12% 100%, 0% 95%)"
              }
            : undefined
        }
      >
        <div>
          {/* Split / Hero Header */}
          <div className="flex items-center justify-between mb-0.5">
            <div className="w-2.5 h-1.5 bg-blue-900 rounded-2xs" />
            <div className="h-1 w-4 bg-blue-950 rounded-2xs" />
          </div>
          <div className="h-0.5 w-full bg-slate-300 rounded-2xs mb-0.5" />
          {/* Customer box */}
          <div className="border border-slate-200 rounded-2xs p-0.5 space-y-0.5 mb-0.5">
            <div className="flex justify-between"><div className="h-0.5 w-3 bg-slate-400" /><div className="h-0.5 w-3.5 bg-blue-800" /></div>
            <div className="h-0.5 w-5 bg-slate-700" />
          </div>
          {/* Solid Navy ₹ Hero Amount Banner */}
          <div className="bg-[#0f2d59] rounded-2xs p-0.5 text-center my-0.5">
            <div className="h-0.5 w-3 bg-amber-400 mx-auto mb-0.5" />
            <div className="h-1 w-5 bg-white mx-auto rounded-2xs" />
          </div>
          {/* Bullion Warm Amber Box */}
          <div className="bg-amber-50 border border-amber-300 rounded-2xs p-0.5 flex justify-between items-center">
            <div className="h-0.5 w-2 bg-amber-700" />
            <div className="h-0.5 w-2 bg-amber-700" />
            <div className="h-0.5 w-2 bg-amber-700" />
          </div>
        </div>
        {/* Footer: QR + Sign */}
        <div className="flex items-center justify-between pt-0.5 border-t border-slate-200">
          <div className="w-2 h-2 bg-slate-100 border border-slate-400 rounded-2xs flex items-center justify-center text-[3px] font-mono leading-none">
            QR
          </div>
          <div className="h-0.5 w-3 bg-slate-700 rounded-2xs" />
        </div>
      </div>
    );
  }

  if (item.id.includes("Receipt") || item.id.includes("Payment")) {
    const isPayment = item.id.includes("Payment");
    const isThermal = item.id.startsWith("thermal");
    const is80mm = item.id.includes("80mm");
    const is58mm = item.id.includes("58mm");
    const isLandscape = item.id.includes("Landscape");
    const isA5 = item.id.startsWith("a5");

    const widthClass = is58mm
      ? "w-[34px] h-[64px]"
      : is80mm
      ? "w-[42px] h-[66px]"
      : isLandscape
      ? "w-[64px] h-[45px]"
      : isA5
      ? "w-[44px] h-[62px]"
      : "w-[48px] h-[66px]";

    return (
      <div
        className={`${widthClass} bg-white rounded shadow-xs ${isThermal ? 'border-x border-t border-slate-900' : 'border-2 border-slate-900'} p-0.5 flex flex-col justify-between shrink-0 font-sans relative`}
        style={
          isThermal
            ? {
                clipPath:
                  "polygon(0% 0%, 100% 0%, 100% 95%, 88% 100%, 75% 95%, 62% 100%, 50% 95%, 38% 100%, 25% 95%, 12% 100%, 0% 95%)"
              }
            : undefined
        }
      >
        <div>
          {/* Centered Title & Shop Header */}
          <div className="text-center mb-0.5">
            <div className={`h-1 mx-auto rounded-2xs ${isPayment ? 'w-4 bg-rose-700' : 'w-4 bg-emerald-700'}`} />
            <div className="h-0.5 w-6 bg-slate-800 mx-auto mt-0.5 rounded-2xs" />
          </div>
          {/* Subheader: No & Date */}
          <div className="flex items-center justify-between px-0.5 mb-0.5 text-[4px]">
            <div className="h-0.5 w-3 bg-slate-700 rounded-2xs" />
            <div className="h-0.5 w-3 bg-slate-500 rounded-2xs" />
          </div>
          {/* Field Lines */}
          <div className="space-y-0.5 px-0.5">
            <div className="flex justify-between"><div className="h-0.5 w-3.5 bg-slate-400" /><div className="h-0.5 w-4 bg-slate-800" /></div>
            <div className="flex justify-between"><div className="h-0.5 w-3.5 bg-slate-400" /><div className="h-0.5 w-5 bg-slate-800" /></div>
            <div className="flex justify-between"><div className="h-0.5 w-4 bg-slate-400" /><div className="h-0.5 w-3.5 bg-slate-700" /></div>
          </div>
          {/* Solid Black ₹ Currency Box */}
          <div className="mt-1 flex items-center px-0.5">
            <div className="w-1.5 h-1.5 bg-black flex items-center justify-center text-[3px] text-white font-bold leading-none">₹</div>
            <div className="h-1.5 w-4.5 border border-black bg-white flex items-center justify-end pr-0.5 text-[2.5px] font-bold">1000</div>
          </div>
        </div>
        {/* Signature at bottom right */}
        <div className="flex justify-end pt-0.5 border-t border-slate-200">
          <div className="h-0.5 w-4 bg-slate-700 rounded-2xs" />
        </div>
      </div>
    );
  }

  if (item.id === "a4Sales") {
    return (
      <div className="w-[48px] h-[66px] bg-white rounded shadow-xs border border-slate-300 p-0.5 flex flex-col justify-between shrink-0">
        <div>
          <div className="h-1 w-7 bg-amber-500/80 rounded-xs mb-0.5" />
          <div className="h-0.5 w-4 bg-slate-300 rounded-xs mb-0.5" />
          <div className="h-1.5 w-full bg-[#0f2d59] rounded-xs mb-0.5" />
          <div className="space-y-0.5">
            <div className="h-0.5 w-full bg-slate-200" />
            <div className="h-0.5 w-full bg-slate-100" />
            <div className="h-0.5 w-full bg-slate-200" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-0.5 border-t border-slate-100">
          <div className="h-1.5 w-4 bg-sky-100 rounded-xs border border-sky-300" />
          <div className="w-2.5 h-2.5 bg-slate-100 border border-slate-400 rounded-xs flex items-center justify-center text-[4px] font-mono leading-none">
            QR
          </div>
        </div>
      </div>
    );
  }

  if (item.id === "a4Purchase") {
    return (
      <div className="w-[48px] h-[66px] bg-white rounded shadow-xs border border-slate-300 p-0.5 flex flex-col justify-between shrink-0">
        <div>
          <div className="h-1 w-8 bg-emerald-600 rounded-xs mb-0.5" />
          <div className="h-0.5 w-5 bg-slate-300 rounded-xs mb-0.5" />
          <div className="h-1.5 w-full bg-[#064e3b] rounded-xs mb-0.5" />
          <div className="space-y-0.5">
            <div className="h-0.5 w-full bg-emerald-100" />
            <div className="h-0.5 w-full bg-slate-100" />
            <div className="h-0.5 w-full bg-emerald-100" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-0.5 border-t border-slate-100">
          <div className="w-2.5 h-2.5 border border-dashed border-emerald-400 rounded-xs flex items-center justify-center text-[3.5px] text-emerald-600 font-bold leading-none">
            TH
          </div>
          <div className="h-0.5 w-4 bg-slate-300 rounded-xs" />
        </div>
      </div>
    );
  }

  if (item.id === "a5Sales" || item.id === "a5Purchase") {
    const isSales = item.id === "a5Sales";
    return (
      <div className="w-[62px] h-[46px] bg-white rounded shadow-xs border border-slate-300 p-0.5 flex flex-col justify-between shrink-0">
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <div className={`h-1 w-6 ${isSales ? 'bg-amber-500' : 'bg-emerald-600'} rounded-xs`} />
            <div className="h-0.5 w-3 bg-slate-300 rounded-xs" />
          </div>
          <div className={`h-1 w-full ${isSales ? 'bg-[#1e293b]' : 'bg-[#064e3b]'} rounded-xs mb-0.5`} />
          <div className="space-y-0.5">
            <div className="h-0.5 w-full bg-slate-200" />
            <div className="h-0.5 w-full bg-slate-100" />
          </div>
        </div>
        <div className="flex items-center justify-between pt-0.5 border-t border-slate-100">
          <div className="h-0.5 w-4 bg-slate-300 rounded-xs" />
          <div className="w-2 h-2 bg-slate-100 border border-slate-400 rounded-xs flex items-center justify-center text-[3.5px] font-mono leading-none">
            QR
          </div>
        </div>
      </div>
    );
  }

  if (item.id === "thermal80mmSale" || item.id === "thermal80mmPurchase") {
    const isSales = item.id === "thermal80mmSale";
    return (
      <div
        className="w-[40px] h-[64px] bg-white border-x border-t border-slate-300 p-0.5 flex flex-col justify-between shadow-xs relative shrink-0"
        style={{
          clipPath: "polygon(0% 0%, 100% 0%, 100% 94%, 88% 100%, 75% 94%, 62% 100%, 50% 94%, 38% 100%, 25% 94%, 12% 100%, 0% 94%)"
        }}
      >
        <div className="space-y-0.5">
          <div className="h-0.5 w-5 bg-slate-800 mx-auto rounded-xs" />
          <div className="h-0.5 w-3 bg-slate-400 mx-auto rounded-xs" />
          <div className="h-0.5 w-full bg-slate-200 my-0.5" />
          <div className="space-y-0.5">
            <div className="h-0.5 w-full bg-slate-200" />
            <div className="h-0.5 w-full bg-slate-100" />
          </div>
        </div>
        <div className="space-y-0.5 mb-1.5">
          <div className="w-2.5 h-2.5 bg-slate-100 border border-slate-400 mx-auto rounded-xs flex items-center justify-center text-[3.5px] font-mono leading-none">
            QR
          </div>
          {isSales && (
            <div className="flex gap-[1px] justify-center h-1.5">
              <div className="w-[1px] h-full bg-slate-700" />
              <div className="w-[1.5px] h-full bg-slate-700" />
              <div className="w-[1px] h-full bg-slate-700" />
              <div className="w-[1.5px] h-full bg-slate-700" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (
    item.id === "thermal58mmSale" ||
    item.id === "thermal58mmPurchase" ||
    item.id === "thermal57mmSale" ||
    item.id === "thermal57mmPurchase"
  ) {
    return (
      <div
        className="w-[32px] h-[62px] bg-white border-x border-t border-slate-300 p-0.5 flex flex-col justify-between shadow-xs relative shrink-0"
        style={{
          clipPath: "polygon(0% 0%, 100% 0%, 100% 95%, 85% 100%, 70% 95%, 55% 100%, 40% 95%, 25% 100%, 10% 95%, 0% 100%)"
        }}
      >
        <div className="space-y-0.5">
          <div className="h-0.5 w-4 bg-slate-800 mx-auto rounded-xs" />
          <div className="h-0.5 w-full bg-slate-200" />
        </div>
        <div className="space-y-0.5 mb-1.5">
          <div className="h-0.5 w-4 bg-amber-200 border border-amber-300 mx-auto rounded-xs" />
          <div className="w-2 h-2 bg-slate-100 border border-slate-400 mx-auto rounded-xs flex items-center justify-center text-[3px] font-mono leading-none">
            QR
          </div>
        </div>
      </div>
    );
  }

  if (item.id === "ratTailTag") {
    return (
      <div className="w-[84px] h-[22px] flex items-center justify-between shrink-0">
        <div className="w-[30px] h-[18px] bg-white border border-purple-400 rounded-xs p-0.5 flex flex-col justify-between shadow-2xs">
          <div className="h-0.5 w-4 bg-purple-500 rounded-xs" />
          <div className="h-0.5 w-5 bg-slate-400 rounded-xs" />
        </div>
        <div className="w-[18px] h-[2px] bg-purple-300" />
        <div className="w-[30px] h-[18px] bg-white border border-purple-400 rounded-xs p-0.5 flex flex-col justify-between shadow-2xs">
          <div className="flex gap-[1px] justify-center h-2 mt-0.5">
            <div className="w-[1px] h-full bg-slate-800" />
            <div className="w-[1.5px] h-full bg-slate-800" />
            <div className="w-[1px] h-full bg-slate-800" />
            <div className="w-[1.5px] h-full bg-slate-800" />
          </div>
          <div className="h-0.5 w-4 bg-slate-500 rounded-xs" />
        </div>
      </div>
    );
  }

  // Default: RFID Tag
  return (
    <div className="w-[58px] h-[38px] bg-white border border-fuchsia-400 rounded-md p-0.5 flex flex-col justify-between shadow-xs shrink-0">
      <div className="flex items-center justify-between">
        <span className="text-[6px] text-fuchsia-600 font-bold leading-none">((•)) RFID</span>
        <div className="w-2 h-2 bg-slate-100 border border-slate-400 rounded-xs flex items-center justify-center text-[3px] font-mono leading-none">
          QR
        </div>
      </div>
      <div className="flex gap-[1px] justify-center h-1.5">
        <div className="w-[1px] h-full bg-slate-700" />
        <div className="w-[1.5px] h-full bg-slate-700" />
        <div className="w-[1px] h-full bg-slate-700" />
        <div className="w-[1.5px] h-full bg-slate-700" />
      </div>
      <div className="h-0.5 w-8 bg-fuchsia-100 rounded-xs" />
    </div>
  );
}

/**
 * 🎨 InvoiceDesigner Main Component:
 * The interactive visual studio modal where users design and customize print templates.
 *
 * @param {Function} onClose - Closes the modal window when the user clicks 'X'
 * @param {number} zIndex - Layering depth ensuring the modal sits on top of all windows
 */
export default function InvoiceDesigner({ onClose, zIndex }) {
  // Backend API URL and database tenant code from context (with fallback)
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode: contextDbCode } = useDbContext();
  const dbCode = contextDbCode || sessionStorage.getItem("dbCode") || localStorage.getItem("dbCode") || "DEFAULT";

  // Permissions
  const permissions = useSelector(selectMenuPermissions);
  const formPermissions = permissions?.["invoiceDesigner"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  // ---------------------------------------------------------------------------------------
  // 🏷️ 1. TEMPLATE IDENTITY STATE (Names, Types, and Storage List)
  // ---------------------------------------------------------------------------------------
  // The user-defined friendly name for this design
  const [templateName, setTemplateName] = useState("A4 Luxury Jewellery Tax Invoice");
  // Which voucher screen will use this design (e.g. "SALE INVOICE", "PURCHASE VOUCHER")
  const [selectedVoucherType, setSelectedVoucherType] = useState("SALE INVOICE");
  // ID of the template currently open (or '__NEW__' if making a fresh one)
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  // List of all templates loaded from the SQL database & browser storage
  const [savedTemplates, setSavedTemplates] = useState([]);
  // Shows a spinner while templates are loading from the database
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // ---------------------------------------------------------------------------------------
  // 📐 2. DYNAMIC PAPER DIMENSIONS STATE (Millimeter Rulers)
  // ---------------------------------------------------------------------------------------
  // Which preset paper is selected ('A4', 'A5', '80MM', '57MM', 'RAT_TAIL', 'CUSTOM', etc.)
  const [selectedPaperId, setSelectedPaperId] = useState("A4");
  // Width of the paper in millimeters
  const [paperWidth, setPaperWidth] = useState(210);
  // Height of the paper in millimeters
  const [paperHeight, setPaperHeight] = useState(297);
  // Margin/Padding around the edge of the paper in millimeters
  const [paperPadding, setPaperPadding] = useState(8);

  // ---------------------------------------------------------------------------------------
  // 🎛️ 3. NAVIGATION & UI CONTROLS STATE
  // ---------------------------------------------------------------------------------------
  // Which tab is open on the left sidebar: 'templates' (saved blueprints) or 'toolbox' (stickers)
  const [activeLeftTab, setActiveLeftTab] = useState("templates");
  // Which tab is open on the right inspector: 'tokens' (word bank), 'symbols' (rupee & coins), or 'styles' (telemetry)
  const [activeInspectorTab, setActiveInspectorTab] = useState("tokens");
  // Search box text to quickly filter tokens (e.g. typing "gold" or "amount")
  const [tokenSearch, setTokenSearch] = useState("");
  // Which token was just copied to the clipboard (for the green checkmark feedback)
  const [copiedToken, setCopiedToken] = useState(null);
  // Search box text to quickly filter symbols & coins
  const [symbolSearch, setSymbolSearch] = useState("");
  // Which symbol was just copied to the clipboard (for visual feedback)
  const [copiedSymbol, setCopiedSymbol] = useState(null);
  // True while the save operation is writing to the database
  const [isSaving, setIsSaving] = useState(false);
  // Current zoom level indicator on canvas
  const [zoomLevel, setZoomLevel] = useState("100%");

  // Zoom preset levels for precision inspection (25% up to 400%)
  const ZOOM_LEVELS = useMemo(() => [25, 50, 75, 100, 125, 150, 200, 250, 300, 400], []);

  /**
   * Increments the zoom level to the next higher preset step (up to 400%)
   */
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => {
      const current = parseFloat(prev) || 100;
      const next = ZOOM_LEVELS.find((z) => z > current);
      return next ? `${next}%` : `${Math.min(400, current + 25)}%`;
    });
  }, [ZOOM_LEVELS]);

  /**
   * Decrements the zoom level to the previous lower preset step (down to 25%)
   */
  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => {
      const current = parseFloat(prev) || 100;
      const prevVal = [...ZOOM_LEVELS].reverse().find((z) => z < current);
      return prevVal ? `${prevVal}%` : `${Math.max(25, current - 25)}%`;
    });
  }, [ZOOM_LEVELS]);

  /**
   * Resets the zoom level back to standard 100% (1:1 ratio)
   */
  const handleZoomReset = useCallback(() => {
    setZoomLevel("100%");
  }, []);

  // Global Keyboard Shortcuts for Zooming (Ctrl + +, Ctrl + -, Ctrl + 0)
  useEffect(() => {
    const handleGlobalZoomKeyDown = (e) => {
      if (
        e.target &&
        (e.target.tagName === "INPUT" ||
          e.target.tagName === "TEXTAREA" ||
          e.target.isContentEditable)
      ) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          handleZoomIn();
        } else if (e.key === "-" || e.key === "_") {
          e.preventDefault();
          handleZoomOut();
        } else if (e.key === "0") {
          e.preventDefault();
          handleZoomReset();
        }
      }
    };

    window.addEventListener("keydown", handleGlobalZoomKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalZoomKeyDown);
  }, [handleZoomIn, handleZoomOut, handleZoomReset]);

  // Floating alert message
  const [studioToast, setStudioToast] = useState(null);

  // Preset Gallery Showcase States
  const [showPresetGallery, setShowPresetGallery] = useState(false);
  const [presetGalleryCategory, setPresetGalleryCategory] = useState("all");
  const [presetSearch, setPresetSearch] = useState("");

  // Print Model Assigner States
  const [showAssignerModal, setShowAssignerModal] = useState(false);
  // In-App User Manual & Guide Modal State
  const [showManualModal, setShowManualModal] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [locationsList, setLocationsList] = useState([{ id: 0, name: "All Showrooms (Global Default)" }]);
  const [assignerLocation, setAssignerLocation] = useState(0);
  const [savingAssignmentVoucher, setSavingAssignmentVoucher] = useState(null);
  const [assignerSearchQuery, setAssignerSearchQuery] = useState("");
  const [assignerCategoryFilter, setAssignerCategoryFilter] = useState("all");
  const [assignerDraftMap, setAssignerDraftMap] = useState({});

  /**
   * 🔔 showNotification:
   * Displays a quick, friendly toast notification in the corner of the screen.
   * Tells the user things like "Template Saved!" or "Copied to Clipboard!".
   */
  const showNotification = useCallback((message, iconType = "info") => {
    setStudioToast(message);
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: iconType,
      title: message,
      showConfirmButton: false,
      timer: 1800
    });
  }, []);

  // Auto-dismiss floating toast banner after 2.4 seconds
  useEffect(() => {
    if (!studioToast) return;
    const t = setTimeout(() => setStudioToast(null), 2400);
    return () => clearTimeout(t);
  }, [studioToast]);

  // ---------------------------------------------------------------------------------------
  // 🖨️ 4. TEST PDF PREVIEW MODAL STATE
  // ---------------------------------------------------------------------------------------
  // Controls whether the test print preview modal popup is open or closed
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  // The temporary browser URL (blob URL) pointing to the generated PDF
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  // True while `@pdfme/generator` is busy baking the PDF
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  // Dynamic header info for preview modal
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewDimensions, setPreviewDimensions] = useState("");

  // ---------------------------------------------------------------------------------------
  // 🎯 5. ACTIVE SELECTION TELEMETRY (Live Ruler)
  // ---------------------------------------------------------------------------------------
  // When you click on any sticker on the canvas, this tells you its exact (X, Y) and size!
  const [activeTelemetry, setActiveTelemetry] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    name: "None",
    type: "",
    alignment: "left",
    verticalAlignment: "top"
  });
  // Micro-nudge increment step (mm): 0.1, 0.5, 1.0, 2.0
  const [nudgeStep, setNudgeStep] = useState(0.5);
  // Phase 2: Multi-selection tracking (array of element names selected on canvas)
  const [selectedElementNames, setSelectedElementNames] = useState([]);
  // Phase 2: Magnetic grid snapping toggle & grid increment (mm: 2 or 5)
  const [snapGridEnabled, setSnapGridEnabled] = useState(false);
  const [gridSize, setGridSize] = useState(2);
  // Faint Alignment Grid Overlay directly above the white canvas sheet
  const [showCanvasOverlayGrid, setShowCanvasOverlayGrid] = useState(true);
  const [overlayGridSpacing, setOverlayGridSpacing] = useState(1); // mm: 1, 2, 5, 10
  const [showCenterCrosshairs, setShowCenterCrosshairs] = useState(true);
  const [showMarginBounds, setShowMarginBounds] = useState(true);

  // ---------------------------------------------------------------------------------------
  // 🏗️ 6. DESIGNER ENGINE REFERENCES
  // ---------------------------------------------------------------------------------------
  // Ref to the HTML `<div>` where the PDFME drawing canvas will live
  const designerContainerRef = useRef(null);
  // Ref holding the active `@pdfme/ui` Designer instance
  const designerRef = useRef(null);
  // Ref keeping track of the current template object in memory
  const activeTemplateRef = useRef(SAMPLE_TEMPLATES.a4Sales.template);
  // Hidden file input ref for uploading showroom brand logo
  const logoFileInputRef = useRef(null);

  // ---------------------------------------------------------------------------------------
  // 🧰 7. PDFME PLUGINS REGISTRY
  // ---------------------------------------------------------------------------------------
  // Registered visual schema plugins:
  // - text: Standard and dynamic token placeholder rendering
  // - image: Embedded base64 graphics and store logos
  // - line: Clean section dividers and borders
  // - rectangle: Background fills and grouping boxes
  // - table: Multi-column itemized grids with header styling
  // - barcodes: Scannable 1D/2D symbologies (QR Code, Code128, EAN13, Code39)
  const plugins = useMemo(() => {
    return {
      text,
      image,
      line,
      rectangle,
      table,
      multiVariableText,
      qrcode: barcodes.qrcode,
      code128: barcodes.code128,
      ean13: barcodes.ean13,
      code39: barcodes.code39
    };
  }, []);

  /**
   * getPresetIdFromDimensions:
   * Maps specified dimensions in millimeters to standard pre-configured paper size IDs.
   * Falls back to "CUSTOM" if no standard preset matches.
   */
  const getPresetIdFromDimensions = useCallback((w, h) => {
    const match = PAPER_SIZES.find((p) => p.id !== "CUSTOM" && p.width === w && p.height === h);
    return match ? match.id : "CUSTOM";
  }, []);

  /**
   * fetchRegisteredTemplates:
   * Synchronizes template registry from both MSSQL backend database and browser local storage.
   * If both sources are empty, seeds standard jewellery sales and purchase presets.
   */
  const fetchRegisteredTemplates = useCallback(async (targetSelectId = null) => {
    setIsLoadingTemplates(true);
    let loadedFromApi = [];

    // Step 1: Attempt to load from the MSSQL database via backend API
    if (apiBaseUrl && dbCode) {
      try {
        const res = await axios.get(`${apiBaseUrl}/main/printtemplates/all/${dbCode}`);
        if (Array.isArray(res.data) && res.data.length > 0) {
          loadedFromApi = res.data.map((t) => ({
            id: t.Auto,
            templateName: t.TemplateName,
            voucherType: t.VoucherType,
            paperSize: t.PaperSize,
            widthMm: t.WidthMm || 57,
            heightMm: t.HeightMm || 130,
            paddingMm: t.PaddingMm || 2,
            templateJson: t.TemplateJson,
            createdAt: t.CreatedAt,
            isActive: t.IsActive === 1
          }));
        }
      } catch (err) {
        console.warn("Backend printtemplates fetch skipped:", err.message);
      }
    }

    // Step 2: Read from browser localStorage
    const localKey = `shergold_print_templates_${dbCode || 'DEFAULT'}`;
    let localSaved = [];
    try {
      localSaved = JSON.parse(localStorage.getItem(localKey) || '[]');
    } catch (_) {
      localSaved = [];
    }

    // Step 3: Combine both sources, giving preference to database records
    const mergedMap = new Map();
    loadedFromApi.forEach((t) => mergedMap.set(t.id || t.templateName, t));
    localSaved.forEach((t) => {
      const key = t.id || t.templateName;
      if (!mergedMap.has(key)) {
        mergedMap.set(key, t);
      }
    });

    let finalList = Array.from(mergedMap.values());

    // Step 4: If nothing exists yet, seed with standard production templates
    if (finalList.length === 0) {
      finalList = [
        {
          id: "tpl_a4_sales",
          templateName: "A4 Luxury Jewellery Tax Invoice",
          voucherType: "SALE INVOICE",
          paperSize: "A4",
          widthMm: 210,
          heightMm: 297,
          paddingMm: 8,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a4Sales.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a4_purchase",
          templateName: "A4 Jewellery Purchase & Old Gold Voucher",
          voucherType: "PURCHASE VOUCHER",
          paperSize: "A4",
          widthMm: 210,
          heightMm: 297,
          paddingMm: 8,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a4Purchase.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a5_sales",
          templateName: "A5 Executive Jewellery Sales Invoice",
          voucherType: "SALE INVOICE",
          paperSize: "A5",
          widthMm: 148,
          heightMm: 210,
          paddingMm: 6,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a5Sales.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a5_purchase",
          templateName: "A5 Old Gold Purchase & Exchange Slip",
          voucherType: "PURCHASE VOUCHER",
          paperSize: "A5",
          widthMm: 148,
          heightMm: 210,
          paddingMm: 6,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a5Purchase.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_80mm_pos_sale",
          templateName: "80mm POS Jewellery Sales Bill",
          voucherType: "SALE INVOICE",
          paperSize: "80MM",
          widthMm: 80,
          heightMm: 160,
          paddingMm: 3,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal80mmSale.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_80mm_purchase",
          templateName: "80mm Old Gold Buying Slip",
          voucherType: "PURCHASE VOUCHER",
          paperSize: "80MM",
          widthMm: 80,
          heightMm: 160,
          paddingMm: 3,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal80mmPurchase.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_58mm_sale",
          templateName: "58mm Compact Jewellery Sales Bill",
          voucherType: "SALE INVOICE",
          paperSize: "58MM",
          widthMm: 58,
          heightMm: 130,
          paddingMm: 2,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal58mmSale.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_58mm_purchase",
          templateName: "58mm Old Gold Buyback Slip",
          voucherType: "PURCHASE VOUCHER",
          paperSize: "58MM",
          widthMm: 58,
          heightMm: 130,
          paddingMm: 2,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal58mmPurchase.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_rattail_tag",
          templateName: "Jewellery Rat-Tail Barcode Tag (100×12)",
          voucherType: "BARCODE TAG",
          paperSize: "RAT_TAIL",
          widthMm: 100,
          heightMm: 12,
          paddingMm: 0.5,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.ratTailTag.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_rfid_tag",
          templateName: "RFID Smart Jewellery Tag (40×25)",
          voucherType: "RFID TAG",
          paperSize: "RFID_TAG",
          widthMm: 40,
          heightMm: 25,
          paddingMm: 1,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.rfidTag.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_58mm_receipt_smart",
          templateName: "58mm POS Smart Receipt (Logo & UPI QR)",
          voucherType: "RECEIPT",
          paperSize: "58MM",
          widthMm: 58,
          heightMm: 165,
          paddingMm: 2,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal58mmReceiptSmart.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_80mm_receipt_smart",
          templateName: "80mm POS Smart Receipt (Logo & UPI QR)",
          voucherType: "RECEIPT",
          paperSize: "80MM",
          widthMm: 80,
          heightMm: 175,
          paddingMm: 3,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal80mmReceiptSmart.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a5_receipt_smart",
          templateName: "A5 Smart Receipt Voucher (Logo & UPI QR)",
          voucherType: "RECEIPT",
          paperSize: "A5",
          widthMm: 148,
          heightMm: 210,
          paddingMm: 6,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a5ReceiptSmart.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a4_receipt_smart",
          templateName: "A4 Smart Receipt Voucher (Logo & UPI QR)",
          voucherType: "RECEIPT",
          paperSize: "A4",
          widthMm: 210,
          heightMm: 297,
          paddingMm: 10,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a4ReceiptSmart.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_58mm_receipt_modern",
          templateName: "58mm Modern Executive Receipt",
          voucherType: "RECEIPT",
          paperSize: "58MM",
          widthMm: 58,
          heightMm: 165,
          paddingMm: 2,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal58mmReceiptModern.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_80mm_receipt_modern",
          templateName: "80mm Modern Executive Receipt",
          voucherType: "RECEIPT",
          paperSize: "80MM",
          widthMm: 80,
          heightMm: 175,
          paddingMm: 3,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.thermal80mmReceiptModern.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a5_receipt_modern",
          templateName: "A5 Modern Executive Receipt Voucher",
          voucherType: "RECEIPT",
          paperSize: "A5",
          widthMm: 148,
          heightMm: 210,
          paddingMm: 6,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a5ReceiptModern.template),
          createdAt: new Date().toISOString()
        },
        {
          id: "tpl_a4_receipt_modern",
          templateName: "A4 Modern Executive Receipt Voucher",
          voucherType: "RECEIPT",
          paperSize: "A4",
          widthMm: 210,
          heightMm: 297,
          paddingMm: 10,
          templateJson: JSON.stringify(SAMPLE_TEMPLATES.a4ReceiptModern.template),
          createdAt: new Date().toISOString()
        }
      ];
      try {
        localStorage.setItem(localKey, JSON.stringify(finalList));
      } catch (_) {}
    }

    setSavedTemplates(finalList);

    // Select the requested template, or the first one in the list
    if (targetSelectId) {
      const match = finalList.find((t) => t.id === targetSelectId || t.templateName === targetSelectId);
      if (match) {
        setSelectedTemplateId(match.id);
      }
    } else if (finalList.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(finalList[0].id);
    }

    setIsLoadingTemplates(false);
  }, [apiBaseUrl, dbCode, selectedTemplateId]);

  // Load templates on first component mount
  useEffect(() => {
    fetchRegisteredTemplates();
  }, [fetchRegisteredTemplates]);

  // ---------------------------------------------------------------------------------------
  // 🖨️ 3B. PRINT MODEL ASSIGNMENTS & LOCATIONS
  // ---------------------------------------------------------------------------------------
  const fetchLocations = useCallback(async () => {
    if (!apiBaseUrl || !dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
      const recs = res.data || [];
      const locs = recs
        .filter((r) => String(r.Type || r.type || "").toLowerCase() === "location")
        .map((r) => ({
          id: Number(r.Auto ?? r.auto ?? 0),
          name: r.Name || r.name || `Location ${r.Auto ?? r.auto}`,
        }));
      setLocationsList([{ id: 0, name: "All Showrooms (Global Default)" }, ...locs]);
    } catch (_) {
      setLocationsList([{ id: 0, name: "All Showrooms (Global Default)" }]);
    }
  }, [apiBaseUrl, dbCode]);

  const fetchAssignments = useCallback(async (targetLoc = assignerLocation) => {
    if (!apiBaseUrl || !dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/printtemplates/assignments/${dbCode}?location=${targetLoc}`);
      const data = res.data || [];
      setAssignments(data);
      localStorage.setItem(`shergold_print_assignments_${dbCode}`, JSON.stringify(data));
    } catch (err) {
      console.warn("Could not fetch assignments from backend:", err.message);
      const cached = localStorage.getItem(`shergold_print_assignments_${dbCode}`);
      if (cached) {
        try { setAssignments(JSON.parse(cached)); } catch (_) {}
      }
    }
  }, [apiBaseUrl, dbCode, assignerLocation]);

  useEffect(() => {
    fetchLocations();
    fetchAssignments();
  }, [fetchLocations, fetchAssignments]);

  const handleAssignTemplate = async (voucherType, templateIdentifier) => {
    if (!templateIdentifier) return;
    setSavingAssignmentVoucher(voucherType);
    try {
      let numericId = null;
      let targetTpl = null;

      // Check if it's a preset key (e.g. preset_a4Sales)
      if (typeof templateIdentifier === "string" && templateIdentifier.startsWith("preset_")) {
        const pKey = templateIdentifier.replace("preset_", "");
        const presetObj = SAMPLE_TEMPLATES[pKey];
        if (presetObj) {
          const existing = savedTemplates.find((t) => t.templateName === presetObj.name);
          if (existing && typeof existing.id === "number") {
            numericId = existing.id;
            targetTpl = existing;
          } else {
            const payload = {
              templateName: presetObj.name,
              voucherType: voucherType,
              paperSize: presetObj.paperSize,
              widthMm: presetObj.width,
              heightMm: presetObj.height,
              paddingMm: presetObj.padding?.[0] || 2,
              templateJson: JSON.stringify(presetObj.template),
              createdBy: "admin"
            };
            if (apiBaseUrl) {
              const saveRes = await axios.post(`${apiBaseUrl}/main/printtemplates/save/${dbCode}`, payload);
              if (saveRes.data?.templateId) {
                numericId = saveRes.data.templateId;
                targetTpl = { ...payload, id: numericId };
                await fetchRegisteredTemplates(numericId);
              }
            }
          }
        }
      } else {
        numericId = Number(templateIdentifier);
        targetTpl = savedTemplates.find((t) => Number(t.id) === numericId);
      }

      if (!numericId) {
        showNotification("Could not determine Template ID for assignment.", "error");
        return;
      }

      if (apiBaseUrl && dbCode) {
        await axios.post(`${apiBaseUrl}/main/printtemplates/assign/${dbCode}`, {
          voucherType,
          templateId: numericId,
          location: Number(assignerLocation) || 0,
          user: "admin"
        });
      }

      const updated = [
        ...assignments.filter((a) => !(a.VoucherType === voucherType && Number(a.Location) === Number(assignerLocation))),
        {
          VoucherType: voucherType,
          TemplateId: numericId,
          Location: Number(assignerLocation) || 0,
          TemplateName: targetTpl?.templateName || targetTpl?.name || `Template #${numericId}`,
          PaperSize: targetTpl?.paperSize || "A4",
          WidthMm: targetTpl?.widthMm || targetTpl?.width || 210,
          HeightMm: targetTpl?.heightMm || targetTpl?.height || 297,
          UpdatedAt: new Date().toISOString()
        }
      ];
      setAssignments(updated);
      localStorage.setItem(`shergold_print_assignments_${dbCode}`, JSON.stringify(updated));

      setSavedTemplates((prev) =>
        prev.map((t) => ({
          ...t,
          isActive: Number(t.id) === numericId || t.isActive
        }))
      );

      showNotification(`Assigned "${targetTpl?.templateName || targetTpl?.name || 'Template'}" to ${voucherType}`, "success");
    } catch (err) {
      console.error("Assign template error:", err);
      showNotification(`Failed to assign: ${err.message}`, "error");
    } finally {
      setSavingAssignmentVoucher(null);
    }
  };

  const handleRemoveAssignment = async (voucherType) => {
    try {
      if (apiBaseUrl && dbCode) {
        await axios.delete(
          `${apiBaseUrl}/main/printtemplates/assignments/${dbCode}/${encodeURIComponent(voucherType)}?location=${assignerLocation}`
        );
      }
      const filtered = assignments.filter((a) => !(a.VoucherType === voucherType && Number(a.Location) === Number(assignerLocation)));
      setAssignments(filtered);
      localStorage.setItem(`shergold_print_assignments_${dbCode}`, JSON.stringify(filtered));
      showNotification(`Unassigned model from ${voucherType}`, "info");
    } catch (err) {
      console.error("Remove assignment error:", err);
      showNotification(`Failed to unassign: ${err.message}`, "error");
    }
  };

  const handleAssignCurrentTemplate = async () => {
    if (!selectedTemplateId || selectedTemplateId === "__NEW__") {
      showNotification("Please save the template first before assigning it to a voucher.", "warning");
      return;
    }
    await handleAssignTemplate(selectedVoucherType, selectedTemplateId);
  };

  const previewTemplatePdf = async (template, title = "Print Preview", dimensions = "") => {
    if (!template) return;
    setPreviewTitle(title);
    setPreviewDimensions(dimensions || `${template.basePdf?.width || 210} × ${template.basePdf?.height || 297} mm`);
    setIsPreviewLoading(true);
    setShowPreviewModal(true);
    try {
      const inputs = resolveTemplateInputs(template, SAMPLE_DATA_MAP);
      const pdfBytes = await generate({
        template,
        inputs,
        plugins,
        options: {
          font: getDefaultFont()
        }
      });
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (err) {
      console.error("Preview render failed:", err);
      Swal.fire({
        icon: "error",
        title: "Preview Error",
        text: err.message
      });
      setShowPreviewModal(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handlePreviewAssignedModel = async (templateId, voucherType) => {
    // 1. Check if it's a factory preset key (e.g. preset_a4Sales)
    if (typeof templateId === "string" && templateId.startsWith("preset_")) {
      const pKey = templateId.replace("preset_", "");
      const presetObj = SAMPLE_TEMPLATES[pKey];
      if (presetObj) {
        await previewTemplatePdf(presetObj.template, `${presetObj.name} (${voucherType})`, `${presetObj.width} × ${presetObj.height} mm`);
        return;
      }
    }

    // 2. Check saved database templates
    const tpl = savedTemplates.find((t) => Number(t.id) === Number(templateId) || t.id === templateId);
    if (tpl && tpl.templateJson) {
      try {
        const parsed = typeof tpl.templateJson === "string" ? JSON.parse(tpl.templateJson) : tpl.templateJson;
        await previewTemplatePdf(parsed, `${tpl.templateName} (${voucherType})`, `${tpl.widthMm || 210} × ${tpl.heightMm || 297} mm`);
        return;
      } catch (e) {
        console.error("Template parse error:", e);
      }
    }

    // 3. Fallback to matching sample template by voucher type
    const preset = Object.values(SAMPLE_TEMPLATES).find((p) => p.voucherType === voucherType) || SAMPLE_TEMPLATES.a4Sales;
    if (preset) {
      await previewTemplatePdf(preset.template, `${preset.name} (${voucherType})`, `${preset.width} × ${preset.height} mm`);
    } else {
      showNotification("No previewable template found for this voucher.", "warning");
    }
  };

  /**
   * INITIALIZE DESIGNER ENGINE:
   * Instantiates the @pdfme/ui Designer engine inside the canvas DOM container.
   * Configures default fonts, templates, and coordinates telemetry listener.
   */
  useEffect(() => {
    if (!designerContainerRef.current) return;

    try {
      const defaultFont = getDefaultFont();
      const instance = new Designer({
        domContainer: designerContainerRef.current,
        template: activeTemplateRef.current,
        plugins,
        options: {
          font: defaultFont
        }
      });

      designerRef.current = instance;

      // Telemetry Listener: Updates ruler coordinates and multi-selection when element(s) are selected on canvas
      instance.onChangeSelection((selected) => {
        const selectionList = selected || [];
        setSelectedElementNames(selectionList);

        if (!selectionList || selectionList.length === 0) {
          setActiveTelemetry({
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            name: "None",
            type: "",
            alignment: "left",
            verticalAlignment: "top"
          });
        } else {
          const current = instance.getTemplate();
          const schemas = current.schemas && current.schemas[0] ? current.schemas[0] : [];
          const firstSchema = schemas.find((s) => s.name === selectionList[0]);
          if (firstSchema) {
            setActiveTelemetry({
              x: Number((firstSchema.position?.x ?? 0).toFixed(1)),
              y: Number((firstSchema.position?.y ?? 0).toFixed(1)),
              width: Number((firstSchema.width ?? 0).toFixed(1)),
              height: Number((firstSchema.height ?? 0).toFixed(1)),
              name: firstSchema.name,
              type: firstSchema.type || "text",
              alignment: firstSchema.alignment || "left",
              verticalAlignment: firstSchema.verticalAlignment || "top"
            });
          }
        }
      });
    } catch (err) {
      console.error("Failed to initialize pdfme designer:", err);
    }

    // Cleanup: Destroy the designer instance on unmount
    return () => {
      if (designerRef.current) {
        try {
          designerRef.current.destroy();
        } catch (_) {}
        designerRef.current = null;
      }
    };
  }, [plugins]);



  /**
   * loadTemplate:
   * Loads a template schema into the interactive canvas and synchronizes
   * top toolbar metadata (template name, voucher type, paper dimensions).
   */
  const loadTemplate = useCallback((templateObject, name, paperId, voucherType) => {
    if (!designerRef.current) return;
    try {
      designerRef.current.updateTemplate(templateObject);
      activeTemplateRef.current = templateObject;

      if (name) setTemplateName(name);
      if (voucherType) setSelectedVoucherType(voucherType);

      const w = templateObject.basePdf?.width || 210;
      const h = templateObject.basePdf?.height || 297;
      const p = templateObject.basePdf?.padding?.[0] || 8;

      setPaperWidth(w);
      setPaperHeight(h);
      setPaperPadding(p);
      setSelectedPaperId(paperId || getPresetIdFromDimensions(w, h));

      showNotification(`Loaded "${name || 'Template'}"`, "success");
    } catch (err) {
      console.error("Error loading template:", err);
      Swal.fire({
        icon: "error",
        title: "Load Failed",
        text: "Could not apply template: " + err.message
      });
    }
  }, [getPresetIdFromDimensions, showNotification]);

  /**
   * handleLoadPreset:
   * Loads selected jewellery preset from the gallery directly into the designer canvas.
   */
  const handleLoadPreset = (presetItem) => {
    const presetData = SAMPLE_TEMPLATES[presetItem.templateKey];
    if (!presetData) return;
    setSelectedTemplateId("__NEW__");
    loadTemplate(presetData.template, presetData.name, presetItem.paperSize, presetItem.voucherType);
    setShowPresetGallery(false);
  };

  /**
   * filteredPresets:
   * Computes filtered jewellery presets based on selected category and text search.
   */
  const filteredPresets = useMemo(() => {
    return PRESET_GALLERY_ITEMS.filter((item) => {
      const matchesCategory = presetGalleryCategory === "all" || item.category === presetGalleryCategory;
      const q = presetSearch.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.name.toLowerCase().includes(q) ||
        item.subtitle.toLowerCase().includes(q) ||
        item.voucherType.toLowerCase().includes(q) ||
        item.paperSize.toLowerCase().includes(q) ||
        item.highlights.some((h) => h.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [presetGalleryCategory, presetSearch]);

  /**
   * handlePreviewPreset:
   * Compiles the selected preset template into a live PDF document using mock data
   * and opens the interactive preview modal directly from the gallery.
   */
  const handlePreviewPreset = async (presetItem) => {
    const presetData = SAMPLE_TEMPLATES[presetItem.templateKey];
    if (!presetData) return;

    setPreviewTitle(`${presetItem.name} (${presetItem.voucherType})`);
    setPreviewDimensions(presetItem.dimensions);
    setIsPreviewLoading(true);
    setShowPreviewModal(true);

    try {
      const template = presetData.template;
      const inputs = resolveTemplateInputs(template, SAMPLE_DATA_MAP);
      const pdfBytes = await generate({
        template,
        inputs,
        plugins,
        options: {
          font: getDefaultFont()
        }
      });

      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (err) {
      console.error("Preset preview failed:", err);
      Swal.fire({
        icon: "error",
        title: "Preset Preview Error",
        text: err.message
      });
      setShowPreviewModal(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  /**
   * handleSelectTemplate:
   * Loads selected saved template record into the visual editor.
   */
  const handleSelectTemplate = (item) => {
    setSelectedTemplateId(item.id);
    setTemplateName(item.templateName);
    setSelectedVoucherType(item.voucherType || "SALE INVOICE");

    let parsedTemplate = null;
    if (item.templateJson) {
      try {
        parsedTemplate = typeof item.templateJson === "string" ? JSON.parse(item.templateJson) : item.templateJson;
      } catch (err) {
        console.error("JSON parse error:", err);
      }
    }

    if (!parsedTemplate) {
      parsedTemplate = SAMPLE_TEMPLATES.a4Sales.template;
    }

    const w = parsedTemplate.basePdf?.width || item.widthMm || 210;
    const h = parsedTemplate.basePdf?.height || item.heightMm || 297;

    loadTemplate(parsedTemplate, item.templateName, item.paperSize || getPresetIdFromDimensions(w, h), item.voucherType);
  };

  /**
   * handleCreateNew:
   * Resets canvas to a new blank template layout.
   */
  const handleCreateNew = () => {
    setSelectedTemplateId("__NEW__");
    setTemplateName("Custom Jewellery Template");
    setSelectedVoucherType("SALE INVOICE");

    const w = paperWidth || 210;
    const h = paperHeight || 297;
    const p = paperPadding || 8;

    const blankTemplate = {
      basePdf: {
        width: w,
        height: h,
        padding: [p, p, p, p]
      },
      schemas: [
        [
          {
            name: "company_header",
            type: "text",
            position: { x: 4, y: 8 },
            width: Math.max(10, w - 8),
            height: 8,
            content: "{{ShowroomName}}",
            fontSize: 10,
            alignment: "center",
            fontColor: "#000000"
          },
          {
            name: "divider_1",
            type: "line",
            position: { x: 4, y: 18 },
            width: Math.max(10, w - 8),
            height: 0.5,
            color: "#000000"
          },
          {
            name: "customer_name",
            type: "text",
            position: { x: 4, y: 22 },
            width: Math.max(10, w - 8),
            height: 7,
            content: "Customer: {{CustomerName}}",
            fontSize: 8,
            alignment: "left",
            fontColor: "#000000"
          }
        ]
      ]
    };

    if (designerRef.current) {
      designerRef.current.updateTemplate(blankTemplate);
      activeTemplateRef.current = blankTemplate;
    }

    showNotification("New template created. Enter custom name & voucher type", "info");
    setTimeout(() => {
      document.getElementById("template-name-input")?.focus();
    }, 100);
  };

  /**
   * handleDimensionChange:
   * Real-time handler for numeric adjustments to width, height, and margins.
   */
  const handleDimensionChange = (field, val) => {
    const num = Math.max(1, Number(val) || 1);
    let newW = paperWidth;
    let newH = paperHeight;
    let newP = paperPadding;

    if (field === "width") newW = num;
    if (field === "height") newH = num;
    if (field === "padding") newP = Math.max(0, Number(val) || 0);

    setPaperWidth(newW);
    setPaperHeight(newH);
    setPaperPadding(newP);
    setSelectedPaperId(getPresetIdFromDimensions(newW, newH));

    if (designerRef.current) {
      const current = designerRef.current.getTemplate();
      const updated = {
        ...current,
        basePdf: {
          width: newW,
          height: newH,
          padding: [newP, newP, newP, newP]
        }
      };
      designerRef.current.updateTemplate(updated);
      activeTemplateRef.current = updated;
    }
  };

  /**
   * handleExtendHeight:
   * Dynamically extends paper height in millimeters for continuous thermal roll formats.
   */
  const handleExtendHeight = (deltaMm) => {
    const updatedH = paperHeight + deltaMm;
    handleDimensionChange("height", updatedH);
    showNotification(`Roll height expanded to ${updatedH} mm`, "info");
  };

  /**
   * handlePaperPresetChange:
   * Applies paper dimensions and loads matching jewellery sales or purchase templates
   * based on the active voucher classification.
   */
  const handlePaperPresetChange = (presetId) => {
    setSelectedPaperId(presetId);
    if (presetId === "CUSTOM") return;

    const spec = PAPER_SIZES.find((p) => p.id === presetId);
    if (!spec || !designerRef.current) return;

    setPaperWidth(spec.width);
    setPaperHeight(spec.height);
    setPaperPadding(spec.padding[0]);

    const isPurchase =
      selectedVoucherType &&
      (selectedVoucherType.toUpperCase().includes("PURCHASE") ||
       selectedVoucherType.toUpperCase().includes("OLD GOLD") ||
       selectedVoucherType.toUpperCase().includes("PAYMENT"));

    let matchingSample = null;
    if (presetId === "58MM" || presetId === "57MM") matchingSample = isPurchase ? SAMPLE_TEMPLATES.thermal58mmPurchase : SAMPLE_TEMPLATES.thermal58mmSale;
    else if (presetId === "80MM") matchingSample = isPurchase ? SAMPLE_TEMPLATES.thermal80mmPurchase : SAMPLE_TEMPLATES.thermal80mmSale;
    else if (presetId === "A4") matchingSample = isPurchase ? SAMPLE_TEMPLATES.a4Purchase : SAMPLE_TEMPLATES.a4Sales;
    else if (presetId === "A5") matchingSample = isPurchase ? SAMPLE_TEMPLATES.a5Purchase : SAMPLE_TEMPLATES.a5Sales;
    else if (presetId === "RAT_TAIL") matchingSample = SAMPLE_TEMPLATES.ratTailTag;
    else if (presetId === "RFID_TAG") matchingSample = SAMPLE_TEMPLATES.rfidTag;

    if (matchingSample) {
      loadTemplate(matchingSample.template, matchingSample.name, presetId, matchingSample.voucherType);
    } else {
      const current = designerRef.current.getTemplate();
      const updated = {
        ...current,
        basePdf: {
          width: spec.width,
          height: spec.height,
          padding: spec.padding
        }
      };
      designerRef.current.updateTemplate(updated);
      activeTemplateRef.current = updated;
      showNotification(`Applied ${spec.name}`, "info");
    }
  };

  /**
   * handleAddPrimitive:
   * Appends a new UI element schema (text, qrcode, code128, line, rectangle, table) to the canvas.
   */
  const handleAddPrimitive = (type, customProps = {}) => {
    if (!designerRef.current) return;
    try {
      const current = designerRef.current.getTemplate();
      const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
      const uid = Date.now().toString().slice(-4);
      let newElement = null;

      // Calculate a smart starting place (spawn position) so stickers don't pile on top of each other
      const spawnX = Math.max(4, Math.round(paperWidth / 4));
      const spawnY = Math.max(10, Math.min(paperHeight - 15, schemas.length * 6 + 10));

      if (type === "text") {
        newElement = {
          name: `text_${uid}`,
          type: "text",
          position: { x: spawnX, y: spawnY },
          width: Math.min(paperWidth - 8, 45),
          height: 7,
          content: customProps.content || "Sample Text Field",
          fontSize: 8,
          alignment: "left",
          fontColor: "#000000",
          ...customProps
        };
      } else if (type === "qrcode") {
        newElement = {
          name: `qrcode_${uid}`,
          type: "qrcode",
          position: customProps.position || { x: spawnX, y: spawnY },
          width: customProps.width || 20,
          height: customProps.height || 20,
          content: customProps.content || "https://shergold.com"
        };
      } else if (type === "image") {
        // High-contrast clean PNG brand logo fallback if no image uploaded yet
        const defaultLogoPlaceholder = customProps.content || DEFAULT_PRESET_LOGO;
        newElement = {
          name: customProps.name || `logo_${uid}`,
          type: "image",
          position: customProps.position || { x: spawnX, y: spawnY },
          width: customProps.width || (paperWidth < 90 ? 25 : 35),
          height: customProps.height || (paperWidth < 90 ? 15 : 20),
          content: defaultLogoPlaceholder
        };
      } else if (type === "code128") {
        newElement = {
          name: `barcode_${uid}`,
          type: "code128",
          position: { x: spawnX, y: spawnY },
          width: Math.min(paperWidth - 8, 45),
          height: 10,
          content: customProps.content || "SG-2026-9901"
        };
      } else if (type === "line") {
        newElement = {
          name: `divider_${uid}`,
          type: "line",
          position: { x: 4, y: spawnY },
          width: Math.max(10, paperWidth - 8),
          height: 0.5,
          color: "#000000"
        };
      } else if (type === "rectangle") {
        newElement = {
          name: `box_${uid}`,
          type: "rectangle",
          position: { x: 4, y: spawnY },
          width: Math.max(10, paperWidth - 8),
          height: 20,
          borderWidth: 0.5,
          borderColor: "#0f172a",
          ...customProps
        };
      } else if (type === "table") {
        // 📊 Table Primitive: Supports 10-column, 5-column, or custom tables!
        const defaultHead10 = [
          "Sl",
          "Item Code",
          "Description",
          "Purity",
          "Gross Wt",
          "Net Wt",
          "Stone Wt",
          "VA %",
          "Rate/g",
          "Total (₹)"
        ];
        const defaultPercentages10 = [5, 10, 20, 8, 8, 8, 8, 7, 11, 15];
        const defaultBody10 = [
          ["1", "RNG-01", "Gold Ring 22K", "916", "4.500", "4.200", "0.300", "3.5%", "6,950", "31,520"],
          ["2", "CHN-09", "Gold Rope Chain", "916", "12.000", "12.000", "0.000", "4.0%", "6,950", "86,736"]
        ];

        const head = customProps.head || defaultHead10;
        const headWidthPercentages = customProps.headWidthPercentages || defaultPercentages10;
        const content = customProps.content || JSON.stringify(defaultBody10);

        // Auto-detect column alignments based on column titles
        const defaultAlignments = {};
        head.forEach((colName, idx) => {
          const lower = colName.toLowerCase();
          if (lower.includes("sl") || lower === "#" || lower.includes("purity") || lower.includes("code")) {
            defaultAlignments[idx] = "center";
          } else if (lower.includes("item") || lower.includes("desc") || lower.includes("name")) {
            defaultAlignments[idx] = "left";
          } else {
            defaultAlignments[idx] = "right";
          }
        });

        newElement = {
          name: customProps.name || `table_${head.length}col_${uid}`,
          type: "table",
          position: customProps.position || { x: 4, y: spawnY },
          width: customProps.width || Math.max(20, paperWidth - 8),
          height: customProps.height || 45,
          showHead: customProps.showHead !== false,
          head,
          headWidthPercentages,
          content,
          tableStyles: customProps.tableStyles || {
            borderWidth: 0.3,
            borderColor: "#334155"
          },
          headStyles: customProps.headStyles || {
            fontName: undefined,
            fontSize: paperWidth < 90 ? 5.5 : 7,
            fontColor: "#ffffff",
            backgroundColor: "#0f2d59",
            borderColor: "#0f2d59",
            borderWidth: { top: 0.2, right: 0.2, bottom: 0.4, left: 0.2 },
            padding: paperWidth < 90 ? { top: 1.2, right: 0.6, bottom: 1.2, left: 0.6 } : { top: 1.8, right: 1, bottom: 1.8, left: 1 },
            alignment: "center",
            verticalAlignment: "middle"
          },
          bodyStyles: customProps.bodyStyles || {
            fontName: undefined,
            fontSize: paperWidth < 90 ? 5 : 6.5,
            fontColor: "#0f172a",
            borderColor: "#cbd5e1",
            borderWidth: { top: 0.1, right: 0.1, bottom: 0.1, left: 0.1 },
            padding: paperWidth < 90 ? { top: 1.0, right: 0.6, bottom: 1.0, left: 0.6 } : { top: 1.5, right: 1, bottom: 1.5, left: 1 },
            alignment: "center",
            verticalAlignment: "middle",
            alternateBackgroundColor: "#f8fafc"
          },
          columnStyles: customProps.columnStyles || {
            alignment: defaultAlignments
          },
          ...customProps
        };
      }

      if (newElement) {
        schemas.push(newElement);
        const updated = {
          ...current,
          schemas: [schemas]
        };
        // Tell the designer canvas to show our newly added sticker!
        designerRef.current.updateTemplate(updated);
        activeTemplateRef.current = updated;

        showNotification(`Added ${newElement.name} to canvas`, "info");
      }
    } catch (err) {
      console.error("Error adding element:", err);
    }
  };

  /**
   * handleLogoFileUpload:
   * Reads selected store logo file (PNG, JPG, SVG, WebP) as base64 DataURL,
   * converts it via canvas to ensure pristine PNG format,
   * and inserts it onto the canvas as an interactive image element.
   */
  const handleLogoFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showNotification("Please select a valid image file (PNG, JPG, SVG, WebP).", "warning");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showNotification("Image exceeds 2MB limit. Please choose a smaller logo file.", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const rawData = event.target.result;
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 200;
          canvas.height = img.naturalHeight || img.height || 100;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const pngData = canvas.toDataURL("image/png");
          handleAddPrimitive("image", {
            name: `logo_${Date.now().toString().slice(-4)}`,
            content: pngData,
            width: paperWidth < 90 ? 25 : 35,
            height: paperWidth < 90 ? 15 : 20
          });
          showNotification("Store logo inserted onto canvas!", "success");
        } catch (_) {
          handleAddPrimitive("image", {
            name: `logo_${Date.now().toString().slice(-4)}`,
            content: rawData,
            width: paperWidth < 90 ? 25 : 35,
            height: paperWidth < 90 ? 15 : 20
          });
          showNotification("Store logo inserted onto canvas!", "success");
        }
      };
      img.onerror = () => {
        handleAddPrimitive("image", {
          name: `logo_${Date.now().toString().slice(-4)}`,
          content: rawData,
          width: paperWidth < 90 ? 25 : 35,
          height: paperWidth < 90 ? 15 : 20
        });
        showNotification("Store logo inserted onto canvas!", "success");
      };
      img.src = rawData;
    };
    reader.onerror = () => {
      showNotification("Failed to read image file.", "error");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, [paperWidth, showNotification, handleAddPrimitive]);

  /**
   * handlePromptCustomTable:
   * Displays an interactive dialog allowing users to define column headers
   * and height for inserting custom tabular itemization grids onto the canvas.
   */
  const handlePromptCustomTable = async () => {
    const { value: formValues } = await Swal.fire({
      title: "Add Custom Table",
      html: `
        <div style="text-align: left; font-size: 12px; line-height: 1.5;">
          <p style="margin-bottom: 8px; color: #475569;">
            Enter comma-separated column headers below (a 10-column jewelry format is pre-filled):
          </p>
          <label style="font-weight: bold; font-size: 11px; color: #1e293b;">Column Headers (Comma separated):</label>
          <textarea id="swal-table-headers" class="swal2-textarea" style="width: 100%; height: 68px; font-size: 12px; margin: 4px 0 10px 0; border: 1px solid #cbd5e1; border-radius: 4px; padding: 6px;">Sl, Item Code, Description, Purity, Gross Wt, Net Wt, Stone Wt, VA %, Rate/g, Total (₹)</textarea>
          
          <label style="font-weight: bold; font-size: 11px; color: #1e293b;">Table Height in mm (default: 45mm):</label>
          <input id="swal-table-height" type="number" class="swal2-input" value="45" min="15" max="250" style="width: 100%; height: 32px; font-size: 12px; margin: 4px 0 6px 0; border: 1px solid #cbd5e1; border-radius: 4px; padding: 4px 8px;" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Add Table to Canvas",
      confirmButtonColor: "#d97706",
      cancelButtonColor: "#64748b",
      focusConfirm: false,
      preConfirm: () => {
        const rawHeaders = document.getElementById("swal-table-headers")?.value;
        const heightVal = Number(document.getElementById("swal-table-height")?.value) || 45;
        if (!rawHeaders || !rawHeaders.trim()) {
          Swal.showValidationMessage("Please enter at least one column header");
          return false;
        }
        const headers = rawHeaders.split(",").map((s) => s.trim()).filter(Boolean);
        if (headers.length === 0) {
          Swal.showValidationMessage("Please enter valid column headers");
          return false;
        }
        return { headers, height: heightVal };
      }
    });

    if (formValues && formValues.headers) {
      const colCount = formValues.headers.length;
      // Proportional widths: make Description wider, Sl narrower, etc.
      let percentages = [];
      if (colCount === 10) {
        percentages = [5, 10, 20, 8, 8, 8, 8, 7, 11, 15];
      } else {
        const basePct = Math.floor(100 / colCount);
        percentages = new Array(colCount).fill(basePct);
        const sum = basePct * colCount;
        percentages[percentages.length - 1] += 100 - sum;
      }

      // Sample row
      const sampleRow = formValues.headers.map((h, i) => i === 0 ? "1" : `${h} 1`);

      handleAddPrimitive("table", {
        name: `table_${colCount}col_${Date.now().toString().slice(-4)}`,
        height: formValues.height,
        head: formValues.headers,
        headWidthPercentages: percentages,
        content: JSON.stringify([sampleRow])
      });
    }
  };

  /**
   * handlePromptUpiQr:
   * Displays an interactive dialog allowing users to configure a valid,
   * scanner-friendly UPI QR code without dealing with manual URL syntax.
   */
  const handlePromptUpiQr = async () => {
    const { value: formValues } = await Swal.fire({
      title: "Add UPI QR Code",
      html: `
        <div style="text-align: left; font-size: 12px; line-height: 1.4;">
          <label style="font-weight: 600; color: #1e293b; display: block; margin-bottom: 4px;">UPI ID (VPA) * :</label>
          <input id="swal-upi-id" class="swal2-input" placeholder="e.g. yourname@oksbi" style="width: 100%; height: 34px; font-size: 13px; margin: 0 0 10px 0; border: 1px solid #cbd5e1; border-radius: 6px; padding: 4px 8px;" />
          
          <label style="font-weight: 600; color: #1e293b; display: block; margin-bottom: 4px;">Business / Payee Name (Optional):</label>
          <input id="swal-upi-name" class="swal2-input" placeholder="e.g. Store Name" style="width: 100%; height: 34px; font-size: 13px; margin: 0 0 10px 0; border: 1px solid #cbd5e1; border-radius: 6px; padding: 4px 8px;" />

          <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0;">
            <input type="checkbox" id="swal-upi-include-amt" style="width: 15px; height: 15px; cursor: pointer;" />
            <label for="swal-upi-include-amt" style="font-size: 11.5px; font-weight: 500; color: #334155; cursor: pointer; user-select: none;">
              Attach dynamic bill amount (<code style="color: #0284c7; font-weight: bold;">&#123;&#123;TotalAmount&#125;&#125;</code>)
            </label>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Add QR to Canvas",
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#64748b",
      focusConfirm: false,
      preConfirm: () => {
        const rawUpi = document.getElementById("swal-upi-id")?.value;
        const rawName = document.getElementById("swal-upi-name")?.value;
        const includeAmt = document.getElementById("swal-upi-include-amt")?.checked;
        if (!rawUpi || !rawUpi.trim()) {
          Swal.showValidationMessage("Please enter your UPI ID");
          return false;
        }
        return { upiId: rawUpi.trim(), payeeName: rawName ? rawName.trim() : "", includeAmt };
      }
    });

    if (formValues && formValues.upiId) {
      let cleanUpi = formValues.upiId;
      if (cleanUpi.startsWith("upi://pay?pa=")) {
        cleanUpi = cleanUpi.replace("upi://pay?pa=", "").split("&")[0];
      }
      let upiUri = `upi://pay?pa=${cleanUpi}`;
      if (formValues.payeeName) {
        upiUri += `&pn=${encodeURIComponent(formValues.payeeName)}`;
      }
      if (formValues.includeAmt) {
        upiUri += `&am={{TotalAmount}}`;
      }
      upiUri += `&cu=INR`;

      handleAddPrimitive("qrcode", {
        name: `upi_qr_${Date.now().toString().slice(-4)}`,
        content: upiUri,
        width: 22,
        height: 22
      });
      showNotification("UPI QR Code added to canvas!", "success");
    }
  };

  /**
   * handleInsertToken:
   * Creates and inserts a text schema pre-populated with the specified system token placeholder.
   */
  const handleInsertToken = (token) => {
    handleAddPrimitive("text", {
      name: `token_${token.key.replace(/[{}]/g, "")}`,
      content: `${token.label}: ${token.key}`
    });
    showNotification(`Inserted token ${token.key}`, "info");
  };

  /**
   * handleCopyToken:
   * Copies token key string (e.g. {{CustomerName}}) to clipboard with visual confirmation.
   */
  const handleCopyToken = (key) => {
    navigator.clipboard.writeText(key);
    setCopiedToken(key);
    showNotification(`Copied ${key} to clipboard`, "success");
    setTimeout(() => setCopiedToken(null), 1800);
  };

  /**
   * handleCopySymbol:
   * Copies symbol string (e.g. ₹, 🪙, 916) to clipboard with visual confirmation.
   */
  const handleCopySymbol = (symbolStr) => {
    navigator.clipboard?.writeText(symbolStr);
    setCopiedSymbol(symbolStr);
    showNotification(`Copied "${symbolStr}" to clipboard`, "success");
    setTimeout(() => setCopiedSymbol(null), 1800);
  };

  /**
   * handleInsertSymbol:
   * Inserts the selected symbol onto the active element (if text selected) or adds a new text element on canvas.
   */
  const handleInsertSymbol = (item) => {
    const symbolText = typeof item === "string" ? item : (item.sampleSnippet || item.symbol);
    const rawSymbol = typeof item === "string" ? item : item.symbol;

    // Check if user has an active text element selected on canvas
    if (designerRef.current && activeTelemetry.name && activeTelemetry.name !== "None") {
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const targetIndex = schemas.findIndex((s) => s.name === activeTelemetry.name);
        if (targetIndex !== -1 && schemas[targetIndex].type === "text") {
          const target = schemas[targetIndex];
          const newContent = target.content ? `${target.content} ${rawSymbol}` : rawSymbol;
          const updatedSchemas = [...schemas];
          updatedSchemas[targetIndex] = {
            ...target,
            content: newContent
          };
          const updatedTemplate = { ...current, schemas: [updatedSchemas] };
          designerRef.current.updateTemplate(updatedTemplate);
          activeTemplateRef.current = updatedTemplate;
          handleCopySymbol(rawSymbol);
          showNotification(`Appended "${rawSymbol}" to ${activeTelemetry.name}`, "success");
          return;
        }
      } catch (err) {
        console.error("Error inserting symbol into selected element:", err);
      }
    }

    // Otherwise, create a clean new text element containing the symbol on canvas
    handleAddPrimitive("text", {
      name: `sym_${Date.now().toString().slice(-4)}`,
      content: symbolText,
      fontSize: symbolText.length <= 4 ? 10 : 8,
      width: Math.max(16, Math.min(paperWidth - 8, Math.round(symbolText.length * 4.5) + 8)),
      height: 7
    });
    handleCopySymbol(rawSymbol);
    showNotification(`Added "${symbolText}" to canvas`, "info");
  };

  /**
   * ─────────────────────────────────────────────────────────────────────────────
   * 📐 PRECISION ALIGNMENT & GEOMETRY MUTATION SUITE
   * ─────────────────────────────────────────────────────────────────────────────
   */

  /**
   * handleUpdateSelectedSchema:
   * Programmatically mutates the currently selected element in the pdfme template
   * (position X/Y, dimensions W/H, text alignment, or vertical alignment)
   * and refreshes the designer canvas instantly without page reload.
   */
  const handleUpdateSelectedSchema = useCallback(
    (updates) => {
      if (!designerRef.current || !activeTelemetry.name || activeTelemetry.name === "None") return;
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const targetIndex = schemas.findIndex((s) => s.name === activeTelemetry.name);
        if (targetIndex === -1) return;

        const target = { ...schemas[targetIndex] };

        if (updates.x !== undefined || updates.y !== undefined) {
          const nextX = updates.x !== undefined ? Math.max(0, parseFloat(updates.x) || 0) : target.position?.x || 0;
          const nextY = updates.y !== undefined ? Math.max(0, parseFloat(updates.y) || 0) : target.position?.y || 0;
          target.position = {
            x: Number(nextX.toFixed(1)),
            y: Number(nextY.toFixed(1))
          };
        }
        if (updates.width !== undefined) {
          target.width = Math.max(1, Number((parseFloat(updates.width) || 1).toFixed(1)));
        }
        if (updates.height !== undefined) {
          target.height = Math.max(0.2, Number((parseFloat(updates.height) || 0.2).toFixed(1)));
        }
        if (updates.alignment !== undefined) {
          target.alignment = updates.alignment;
        }
        if (updates.verticalAlignment !== undefined) {
          target.verticalAlignment = updates.verticalAlignment;
        }

        schemas[targetIndex] = target;
        const updatedTemplate = {
          ...current,
          schemas: [schemas]
        };

        designerRef.current.updateTemplate(updatedTemplate);
        activeTemplateRef.current = updatedTemplate;

        setActiveTelemetry((prev) => ({
          ...prev,
          x: target.position?.x !== undefined ? Number(target.position.x.toFixed(1)) : prev.x,
          y: target.position?.y !== undefined ? Number(target.position.y.toFixed(1)) : prev.y,
          width: target.width !== undefined ? Number(target.width.toFixed(1)) : prev.width,
          height: target.height !== undefined ? Number(target.height.toFixed(1)) : prev.height,
          alignment: target.alignment || prev.alignment || "left",
          verticalAlignment: target.verticalAlignment || prev.verticalAlignment || "top",
          type: target.type || prev.type
        }));
      } catch (err) {
        console.error("Failed to update selected schema:", err);
      }
    },
    [activeTelemetry.name]
  );

  /**
   * One-Click Page Alignment Helpers:
   */
  const handleAlignPageCenterH = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const w = parseFloat(activeTelemetry.width) || 10;
    const targetX = Math.max(0, Number(((paperWidth - w) / 2).toFixed(1)));
    handleUpdateSelectedSchema({ x: targetX });
  };

  const handleAlignPageCenterV = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const h = parseFloat(activeTelemetry.height) || 10;
    const targetY = Math.max(0, Number(((paperHeight - h) / 2).toFixed(1)));
    handleUpdateSelectedSchema({ y: targetY });
  };

  const handleAlignLeftMargin = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const pad = Number((paperPadding || 0).toFixed(1));
    handleUpdateSelectedSchema({ x: pad });
  };

  const handleAlignRightMargin = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const pad = paperPadding || 0;
    const w = parseFloat(activeTelemetry.width) || 10;
    const targetX = Math.max(0, Number((paperWidth - pad - w).toFixed(1)));
    handleUpdateSelectedSchema({ x: targetX });
  };

  const handleAlignTopMargin = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const pad = Number((paperPadding || 0).toFixed(1));
    handleUpdateSelectedSchema({ y: pad });
  };

  const handleAlignBottomMargin = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const pad = paperPadding || 0;
    const h = parseFloat(activeTelemetry.height) || 10;
    const targetY = Math.max(0, Number((paperHeight - pad - h).toFixed(1)));
    handleUpdateSelectedSchema({ y: targetY });
  };

  const handleFitPageWidth = () => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const pad = paperPadding || 0;
    const targetX = Number(pad.toFixed(1));
    const targetW = Math.max(10, Number((paperWidth - pad * 2).toFixed(1)));
    handleUpdateSelectedSchema({ x: targetX, width: targetW });
  };

  const handleNudge = (dx, dy) => {
    if (!activeTelemetry.name || activeTelemetry.name === "None") return;
    const curX = parseFloat(activeTelemetry.x) || 0;
    const curY = parseFloat(activeTelemetry.y) || 0;
    const newX = Math.max(0, Number((curX + dx).toFixed(1)));
    const newY = Math.max(0, Number((curY + dy).toFixed(1)));
    handleUpdateSelectedSchema({ x: newX, y: newY });
  };

  /**
   * ─────────────────────────────────────────────────────────────────────────────
   * 🎛️ PHASE 2: MULTI-ELEMENT ALIGNMENT, DISTRIBUTION & MAGNETIC GRID
   * ─────────────────────────────────────────────────────────────────────────────
   */

  /**
   * handleMultiAlign:
   * Aligns multiple selected elements relative to one another:
   * - 'left': Flush left edges (min X)
   * - 'right': Flush right edges (max right edge)
   * - 'top': Flush top edges (min Y)
   * - 'bottom': Flush bottom edges (max bottom edge)
   * - 'centerH': Center horizontally along common vertical axis
   * - 'centerV': Center vertically along common horizontal axis
   */
  const handleMultiAlign = useCallback(
    (mode) => {
      if (!designerRef.current || selectedElementNames.length < 2) {
        showNotification("Select at least 2 elements (Shift+Click) to align together", "warning");
        return;
      }
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const selectedSet = new Set(selectedElementNames);
        const targetIndices = [];
        const targets = [];

        schemas.forEach((s, idx) => {
          if (selectedSet.has(s.name)) {
            targetIndices.push(idx);
            targets.push({ ...s });
          }
        });

        if (targets.length < 2) return;

        if (mode === "left") {
          const minX = Math.min(...targets.map((t) => t.position?.x ?? 0));
          targets.forEach((t) => {
            t.position = { ...t.position, x: Number(minX.toFixed(1)) };
          });
        } else if (mode === "right") {
          const maxRight = Math.max(...targets.map((t) => (t.position?.x ?? 0) + (t.width ?? 0)));
          targets.forEach((t) => {
            t.position = { ...t.position, x: Number((maxRight - (t.width ?? 0)).toFixed(1)) };
          });
        } else if (mode === "top") {
          const minY = Math.min(...targets.map((t) => t.position?.y ?? 0));
          targets.forEach((t) => {
            t.position = { ...t.position, y: Number(minY.toFixed(1)) };
          });
        } else if (mode === "bottom") {
          const maxBottom = Math.max(...targets.map((t) => (t.position?.y ?? 0) + (t.height ?? 0)));
          targets.forEach((t) => {
            t.position = { ...t.position, y: Number((maxBottom - (t.height ?? 0)).toFixed(1)) };
          });
        } else if (mode === "centerH") {
          const avgCenterX =
            targets.reduce((acc, t) => acc + (t.position?.x ?? 0) + (t.width ?? 0) / 2, 0) / targets.length;
          targets.forEach((t) => {
            t.position = { ...t.position, x: Number((avgCenterX - (t.width ?? 0) / 2).toFixed(1)) };
          });
        } else if (mode === "centerV") {
          const avgCenterY =
            targets.reduce((acc, t) => acc + (t.position?.y ?? 0) + (t.height ?? 0) / 2, 0) / targets.length;
          targets.forEach((t) => {
            t.position = { ...t.position, y: Number((avgCenterY - (t.height ?? 0) / 2).toFixed(1)) };
          });
        }

        targets.forEach((t, i) => {
          schemas[targetIndices[i]] = t;
        });

        const updatedTemplate = { ...current, schemas: [schemas] };
        designerRef.current.updateTemplate(updatedTemplate);
        activeTemplateRef.current = updatedTemplate;
        showNotification(`Aligned ${targets.length} elements (${mode})`, "info");
      } catch (err) {
        console.error("Multi-alignment error:", err);
      }
    },
    [selectedElementNames, showNotification]
  );

  /**
   * handleDistribute:
   * Distributes multiple selected elements evenly across vertical or horizontal axis.
   */
  const handleDistribute = useCallback(
    (axis) => {
      if (!designerRef.current || selectedElementNames.length < 3) {
        showNotification("Select at least 3 elements to distribute evenly", "warning");
        return;
      }
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const selectedSet = new Set(selectedElementNames);
        const targets = [];

        schemas.forEach((s, idx) => {
          if (selectedSet.has(s.name)) {
            targets.push({ ...s, _origIndex: idx });
          }
        });

        if (targets.length < 3) return;

        if (axis === "vertical") {
          targets.sort((a, b) => (a.position?.y ?? 0) - (b.position?.y ?? 0));
          const first = targets[0];
          const last = targets[targets.length - 1];
          const startY = first.position?.y ?? 0;
          const totalSpan = (last.position?.y ?? 0) + (last.height ?? 0) - startY;
          const totalHeights = targets.reduce((acc, t) => acc + (t.height ?? 0), 0);
          const gap = Math.max(0, (totalSpan - totalHeights) / (targets.length - 1));

          let currentY = startY;
          targets.forEach((t) => {
            t.position = { ...t.position, y: Number(currentY.toFixed(1)) };
            currentY += (t.height ?? 0) + gap;
          });
        } else if (axis === "horizontal") {
          targets.sort((a, b) => (a.position?.x ?? 0) - (b.position?.x ?? 0));
          const first = targets[0];
          const last = targets[targets.length - 1];
          const startX = first.position?.x ?? 0;
          const totalSpan = (last.position?.x ?? 0) + (last.width ?? 0) - startX;
          const totalWidths = targets.reduce((acc, t) => acc + (t.width ?? 0), 0);
          const gap = Math.max(0, (totalSpan - totalWidths) / (targets.length - 1));

          let currentX = startX;
          targets.forEach((t) => {
            t.position = { ...t.position, x: Number(currentX.toFixed(1)) };
            currentX += (t.width ?? 0) + gap;
          });
        }

        targets.forEach((t) => {
          const origIdx = t._origIndex;
          delete t._origIndex;
          schemas[origIdx] = t;
        });

        const updatedTemplate = { ...current, schemas: [schemas] };
        designerRef.current.updateTemplate(updatedTemplate);
        activeTemplateRef.current = updatedTemplate;
        showNotification(`Distributed ${targets.length} elements evenly (${axis})`, "info");
      } catch (err) {
        console.error("Distribution error:", err);
      }
    },
    [selectedElementNames, showNotification]
  );

  /**
   * handleMatchSize:
   * Harmonizes dimensions across selected elements (max width, min width, max height, square).
   */
  const handleMatchSize = useCallback(
    (dimension) => {
      if (!designerRef.current || selectedElementNames.length < 2) {
        showNotification("Select at least 2 elements to match size", "warning");
        return;
      }
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const selectedSet = new Set(selectedElementNames);
        const targetIndices = [];
        const targets = [];

        schemas.forEach((s, idx) => {
          if (selectedSet.has(s.name)) {
            targetIndices.push(idx);
            targets.push({ ...s });
          }
        });

        if (targets.length < 2) return;

        if (dimension === "width-max") {
          const maxW = Math.max(...targets.map((t) => t.width ?? 1));
          targets.forEach((t) => {
            t.width = Number(maxW.toFixed(1));
          });
        } else if (dimension === "width-min") {
          const minW = Math.min(...targets.map((t) => t.width ?? 1));
          targets.forEach((t) => {
            t.width = Number(minW.toFixed(1));
          });
        } else if (dimension === "height-max") {
          const maxH = Math.max(...targets.map((t) => t.height ?? 0.5));
          targets.forEach((t) => {
            t.height = Number(maxH.toFixed(1));
          });
        } else if (dimension === "square") {
          targets.forEach((t) => {
            const side = Math.max(t.width ?? 10, t.height ?? 10);
            t.width = Number(side.toFixed(1));
            t.height = Number(side.toFixed(1));
          });
        }

        targets.forEach((t, i) => {
          schemas[targetIndices[i]] = t;
        });

        const updatedTemplate = { ...current, schemas: [schemas] };
        designerRef.current.updateTemplate(updatedTemplate);
        activeTemplateRef.current = updatedTemplate;
        showNotification(`Matched ${dimension} for ${targets.length} elements`, "info");
      } catch (err) {
        console.error("Match size error:", err);
      }
    },
    [selectedElementNames, showNotification]
  );

  /**
   * handleSnapToGrid:
   * Snaps selected element(s) to the active grid step (2mm, 5mm).
   */
  const handleSnapToGrid = useCallback(
    (step = gridSize) => {
      if (!designerRef.current) return;
      try {
        const current = designerRef.current.getTemplate();
        const schemas = current.schemas && current.schemas[0] ? [...current.schemas[0]] : [];
        const targetNames =
          selectedElementNames.length > 0
            ? selectedElementNames
            : activeTelemetry.name !== "None"
            ? [activeTelemetry.name]
            : [];

        if (targetNames.length === 0) {
          showNotification("Select element(s) on canvas to snap to grid", "warning");
          return;
        }

        const nameSet = new Set(targetNames);
        let count = 0;
        schemas.forEach((s) => {
          if (nameSet.has(s.name)) {
            const rawX = s.position?.x ?? 0;
            const rawY = s.position?.y ?? 0;
            const snappedX = Math.round(rawX / step) * step;
            const snappedY = Math.round(rawY / step) * step;
            s.position = {
              x: Number(snappedX.toFixed(1)),
              y: Number(snappedY.toFixed(1))
            };
            count++;
          }
        });

        const updatedTemplate = { ...current, schemas: [schemas] };
        designerRef.current.updateTemplate(updatedTemplate);
        activeTemplateRef.current = updatedTemplate;
        showNotification(`Snapped ${count} element(s) to ${step}mm grid`, "success");
      } catch (err) {
        console.error("Snap to grid error:", err);
      }
    },
    [gridSize, selectedElementNames, activeTelemetry.name, showNotification]
  );

  /**
   * handleTestRenderPdf:
   * Compiles the active canvas template into a live PDF document using mock dataset
   * and opens the interactive preview viewer modal.
   */
  const handleTestRenderPdf = async () => {
    if (!designerRef.current) return;
    setPreviewTitle(`${templateName} (${selectedVoucherType})`);
    setPreviewDimensions(`${paperWidth} × ${paperHeight} mm`);
    setIsPreviewLoading(true);
    setShowPreviewModal(true);

    try {
      const template = designerRef.current.getTemplate();
      const inputs = resolveTemplateInputs(template, SAMPLE_DATA_MAP);
      const pdfBytes = await generate({
        template,
        inputs,
        plugins,
        options: {
          font: getDefaultFont()
        }
      });

      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (err) {
      console.error("Test render failed:", err);
      Swal.fire({
        icon: "error",
        title: "Test PDF Error",
        text: err.message
      });
      setShowPreviewModal(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  /**
   * handleSaveTemplate:
   * Persists the active canvas template to both browser localStorage and
   * the backend MSSQL database table dbo.PrintTemplates via API endpoint.
   */
  const handleSaveTemplate = async () => {
    if (!designerRef.current) return;

    const trimmedName = templateName.trim();
    if (!trimmedName) {
      alert("Please enter a custom Template Name.");
      return;
    }

    const trimmedType = selectedVoucherType.trim().toUpperCase();
    if (!trimmedType) {
      alert("Please enter a Voucher / Scheme Entry Type.");
      return;
    }

    if (!hasSavePermission && !hasEditPermission) {
      showNotification("You do not have permission to save or edit templates.", "warning");
      return;
    }

    setIsSaving(true);

    try {
      const finalTemplate = designerRef.current.getTemplate();
      
      // Ensure basePdf has accurate current paper dimensions
      finalTemplate.basePdf = {
        width: paperWidth,
        height: paperHeight,
        padding: [paperPadding, paperPadding, paperPadding, paperPadding]
      };

      let parsedNumericId = Number(selectedTemplateId);
      let validNumericId = selectedTemplateId !== "__NEW__" && !isNaN(parsedNumericId) && parsedNumericId > 0 ? parsedNumericId : undefined;

      // If selectedTemplateId was non-numeric (e.g. factory preset), check if a template with this exact name already exists in registered templates with a numeric ID
      if (!validNumericId && Array.isArray(savedTemplates)) {
        const existingTpl = savedTemplates.find(
          (t) => (t.templateName || "").trim().toLowerCase() === trimmedName.toLowerCase() && Number(t.id) > 0
        );
        if (existingTpl) {
          validNumericId = Number(existingTpl.id);
        }
      }

      const payload = {
        templateId: validNumericId,
        voucherType: trimmedType,
        templateName: trimmedName,
        paperSize: selectedPaperId === "CUSTOM" ? `CUSTOM (${paperWidth}×${paperHeight}mm)` : selectedPaperId,
        widthMm: paperWidth,
        heightMm: paperHeight,
        paddingMm: paperPadding,
        templateJson: JSON.stringify(finalTemplate),
        dbCode: dbCode || "DEFAULT",
        updatedAt: new Date().toISOString()
      };

      // 1. Save to localStorage
      const localKey = `shergold_print_templates_${dbCode || 'DEFAULT'}`;
      let localList = [];
      try {
        localList = JSON.parse(localStorage.getItem(localKey) || '[]');
      } catch (_) {
        localList = [];
      }

      const existingIndex = localList.findIndex(
        (t) => (payload.templateId && t.id === payload.templateId) || t.templateName === trimmedName
      );

      const recordId = payload.templateId || `local_${Date.now()}`;
      const recordToSave = { ...payload, id: recordId };

      if (existingIndex >= 0) {
        localList[existingIndex] = recordToSave;
      } else {
        localList.unshift(recordToSave);
      }
      localStorage.setItem(localKey, JSON.stringify(localList));

      // 2. Attempt MSSQL API persistence
      let apiSuccess = false;
      if (apiBaseUrl) {
        try {
          const res = await axios.post(`${apiBaseUrl}/main/printtemplates/save/${dbCode}`, payload);
          if (res.data?.success) {
            apiSuccess = true;
            if (res.data?.templateId) {
              const numericId = Number(res.data.templateId);
              recordToSave.id = numericId;
              recordToSave.templateId = numericId;
              const idx = localList.findIndex((t) => t.templateName === trimmedName);
              if (idx >= 0) {
                localList[idx].id = numericId;
                localList[idx].templateId = numericId;
                localStorage.setItem(localKey, JSON.stringify(localList));
              }
            }
          }
        } catch (apiErr) {
          console.warn("Backend API sync deferred; stored in local storage.", apiErr.message);
        }
      }

      // Synchronize any active assignments referencing this template (by ID or Name)
      try {
        const assignKey = `shergold_print_assignments_${dbCode || "DEFAULT"}`;
        const rawAssign = localStorage.getItem(assignKey);
        if (rawAssign) {
          const assignList = JSON.parse(rawAssign);
          let assignUpdated = false;
          assignList.forEach((a) => {
            const matchesId = Number(a.TemplateId) === Number(recordToSave.id) || a.TemplateId === selectedTemplateId;
            const matchesName = (a.TemplateName || "").trim().toLowerCase() === trimmedName.toLowerCase();
            if (matchesId || matchesName) {
              a.TemplateId = recordToSave.id;
              a.TemplateName = trimmedName;
              a.PaperSize = payload.paperSize;
              a.WidthMm = payload.widthMm;
              a.HeightMm = payload.heightMm;
              a.UpdatedAt = new Date().toISOString();
              assignUpdated = true;
            }
          });
          if (assignUpdated) {
            localStorage.setItem(assignKey, JSON.stringify(assignList));
          }
        }
      } catch (_) {}

      // 3. Invalidate print cache and broadcast real-time update to all vouchers & tabs
      clearPrintTemplateCache();
      notifyPrintTemplateUpdated({
        dbCode: dbCode || "DEFAULT",
        voucherType: trimmedType,
        templateId: recordToSave.id
      });

      // 4. Refresh registered list so newly saved template appears in Left Rail
      await fetchRegisteredTemplates(recordToSave.id);
      setSelectedTemplateId(recordToSave.id);

      Swal.fire({
        icon: "success",
        title: "Template Saved Successfully",
        html: `
          <div style="text-align: left; font-size: 13px; line-height: 1.6;">
            <p><strong>Template Name:</strong> ${payload.templateName}</p>
            <p><strong>Voucher Type:</strong> <span style="background:#e0f2fe;color:#0369a1;padding:1px 6px;border-radius:4px;font-weight:bold">${payload.voucherType}</span></p>
            <p><strong>Paper Dimensions:</strong> ${payload.widthMm} × ${payload.heightMm} mm (Margin: ${payload.paddingMm}mm)</p>
            <p><strong>Storage Status:</strong> ${
              apiSuccess
                ? '<span style="color:#15803d;font-weight:bold">✓ Persisted to MSSQL Database (dbo.PrintTemplates)</span>'
                : '<span style="color:#b45309;font-weight:bold">✓ Stored locally & ready for MSSQL sync</span>'
            }</p>
          </div>
        `,
        confirmButtonColor: "#d97706"
      });

      showNotification(`Saved "${trimmedName}" (${trimmedType})`, "success");
    } catch (err) {
      console.error("Save error:", err);
      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: err.message
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * handleDeleteTemplate:
   * Removes template record from both backend MSSQL database and browser localStorage.
   */
  const handleDeleteTemplate = async (item, e) => {
    e.stopPropagation();
    if (!hasDeletePermission) {
      showNotification("You do not have permission to delete templates.", "warning");
      return;
    }
    const result = await Swal.fire({
      title: "Delete Template?",
      text: `Are you sure you want to remove "${item.templateName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete"
    });

    if (result.isConfirmed) {
      if (item.id && typeof item.id === "number" && apiBaseUrl && dbCode) {
        try {
          await axios.delete(`${apiBaseUrl}/main/printtemplates/delete/${dbCode}/${item.id}`);
        } catch (err) {
          console.warn("Delete on backend:", err.message);
        }
      }

      const localKey = `shergold_print_templates_${dbCode || 'DEFAULT'}`;
      const filtered = savedTemplates.filter((t) => t.id !== item.id);
      setSavedTemplates(filtered);
      localStorage.setItem(localKey, JSON.stringify(filtered));
      clearPrintTemplateCache(item.voucherType);
      notifyPrintTemplateUpdated({
        dbCode: dbCode || "DEFAULT",
        voucherType: item.voucherType,
        templateId: item.id,
        action: "delete"
      });

      showNotification(`Deleted "${item.templateName}"`, "info");
      if (selectedTemplateId === item.id && filtered.length > 0) {
        handleSelectTemplate(filtered[0]);
      }
    }
  };

  /**
   * handleExportJson:
   * Serializes current template schema into a downloadable JSON file.
   */
  const handleExportJson = () => {
    if (!designerRef.current) return;
    const template = designerRef.current.getTemplate();
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(template, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `${templateName.replace(/\s+/g, "_")}_schema.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    showNotification("Exported template schema JSON", "success");
  };

  /**
   * filteredCategories:
   * Filters available token dictionary based on inspector search input.
   */
  const filteredCategories = useMemo(() => {
    if (!tokenSearch.trim()) return TOKEN_CATEGORIES;
    const q = tokenSearch.toLowerCase();
    return TOKEN_CATEGORIES.map((cat) => ({
      ...cat,
      tokens: cat.tokens.filter(
        (t) =>
          t.label.toLowerCase().includes(q) ||
          t.key.toLowerCase().includes(q) ||
          t.desc.toLowerCase().includes(q)
      )
    })).filter((cat) => cat.tokens.length > 0);
  }, [tokenSearch]);

  /**
   * filteredSymbolCategories:
   * Filters available symbol dictionary based on symbol search input.
   */
  const filteredSymbolCategories = useMemo(() => {
    if (!symbolSearch.trim()) return SYMBOL_CATEGORIES;
    const q = symbolSearch.toLowerCase();
    return SYMBOL_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.symbol.toLowerCase().includes(q) ||
          item.label.toLowerCase().includes(q) ||
          (item.desc && item.desc.toLowerCase().includes(q)) ||
          (item.code && item.code.toLowerCase().includes(q)) ||
          (item.sampleSnippet && item.sampleSnippet.toLowerCase().includes(q))
      )
    })).filter((cat) => cat.items.length > 0);
  }, [symbolSearch]);

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[920px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">
        
        {/* ── 1. Window Header (SherGold ERP Window Standard) ──────────────────── */}
        <WindowHeader
          title="Invoice & Receipt Designer Studio — Jewellery Print Architecture"
          logo={headeLogo}
          onClose={onClose}
          className="bg-gradient-to-r from-[#142e74] via-[#1a3a8f] to-[#1e40af] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── 2. Studio Action Toolbar ────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200 px-3 py-1.5 flex flex-wrap items-center justify-between gap-2 shadow-2xs shrink-0">
          
          {/* Studio Branding & Metadata */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-2xs">
              <FiLayers className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <h1 className="text-xs font-bold text-slate-900 tracking-tight" style={INTER}>
                  Invoice &amp; Print Designer Studio
                </h1>
                <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1 animate-studio-pulse"></span>
                  MSSQL READY
                </span>
              </div>
              <p className="text-[10px] text-slate-500" style={INTER}>
                Dynamic Paper Sizing, Custom Voucher Types &amp; Visual Layout Studio
              </p>
            </div>
          </div>

          {/* Controls: Custom Name, Voucher Type, Paper Dimensions, CTAs */}
          <div className="flex items-center flex-wrap gap-1.5">
            
            {/* 1. Custom Template Name Input */}
            <div className="flex items-center bg-slate-50 border border-slate-300 rounded px-2 py-0.5 shadow-2xs focus-within:border-amber-500">
              <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500 mr-1.5">
                Template
              </span>
              <input
                id="template-name-input"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="bg-transparent border-0 p-0 text-xs font-semibold text-slate-800 focus:outline-none w-44 placeholder-slate-400"
                placeholder="Enter custom template name..."
                style={INTER}
              />
            </div>

            {/* 2. Custom Voucher / Entry Type Input with Datalist (SmsSettings pattern) */}
            <div className="flex items-center bg-slate-50 border border-slate-300 rounded px-2 py-0.5 shadow-2xs focus-within:border-blue-500">
              <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500 mr-1 flex items-center gap-1">
                <FiTag className="text-blue-600 w-3 h-3" />
                Type *
              </span>
              <input
                id="voucher-type-input"
                type="text"
                list="voucher-type-suggestions"
                value={selectedVoucherType}
                onChange={(e) => setSelectedVoucherType(e.target.value.toUpperCase())}
                placeholder="e.g. SCHEME RECEIPT"
                className="bg-transparent border-0 p-0 text-xs font-bold text-slate-800 focus:outline-none w-36 uppercase placeholder-slate-400"
                style={INTER}
              />
              <datalist id="voucher-type-suggestions">
                {STANDARD_VOUCHER_TYPES.map((t) => (
                  <option key={t} value={t} />
                ))}
                {Array.from(new Set(savedTemplates.map((t) => t.voucherType))).map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </div>

            {/* Active Model Indicator / Quick Assigner */}
            {(() => {
              const currentAssigned = assignments.find(
                (a) => a.VoucherType === selectedVoucherType && (Number(a.Location) === Number(assignerLocation) || Number(a.Location) === 0)
              );
              const isCurrentTemplateActive =
                currentAssigned &&
                selectedTemplateId &&
                Number(currentAssigned.TemplateId) === Number(selectedTemplateId);

              if (isCurrentTemplateActive) {
                return (
                  <span
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-300 text-[10px] font-bold shadow-2xs"
                    title={`This template is the active print model for ${selectedVoucherType}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    Active Model
                  </span>
                );
              }
              if (selectedTemplateId && selectedTemplateId !== "__NEW__") {
                return (
                  <button
                    type="button"
                    onClick={handleAssignCurrentTemplate}
                    disabled={savingAssignmentVoucher === selectedVoucherType}
                    className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-bold transition cursor-pointer shadow-2xs active:scale-95"
                    title={`Set this template as default print model for ${selectedVoucherType}`}
                  >
                    <FiCheck className="w-3 h-3 text-emerald-600" />
                    <span>Set as Active</span>
                  </button>
                );
              }
              return null;
            })()}

            {/* 3. Paper Size Preset & Custom Dimensions Adjuster */}
            <div className="flex items-center bg-slate-50 border border-slate-300 rounded px-2 py-0.5 shadow-2xs gap-1.5">
              <div className="flex items-center gap-1">
                <FiFileText className="text-amber-600 w-3 h-3 shrink-0" />
                <select
                  value={selectedPaperId}
                  onChange={(e) => handlePaperPresetChange(e.target.value)}
                  className="bg-transparent border-0 p-0 text-xs font-semibold text-slate-800 focus:outline-none cursor-pointer max-w-[130px]"
                  style={INTER}
                  title="Select standard paper preset"
                >
                  {PAPER_SIZES.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <span className="text-slate-300">|</span>

              {/* Direct Numeric Width (mm) */}
              <div className="flex items-center gap-1 text-[11px] text-slate-600 font-medium">
                <span title="Width in millimeters">W:</span>
                <input
                  type="number"
                  min="10"
                  max="600"
                  value={paperWidth}
                  onChange={(e) => handleDimensionChange("width", e.target.value)}
                  className="w-12 h-5 px-1 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded text-center focus:border-amber-500 focus:outline-none"
                  title="Width in millimeters"
                />
                <span className="text-[10px] text-slate-400">mm</span>
              </div>

              {/* Direct Numeric Height (mm) */}
              <div className="flex items-center gap-1 text-[11px] text-slate-600 font-medium">
                <span title="Height in millimeters">H:</span>
                <input
                  type="number"
                  min="10"
                  max="2500"
                  value={paperHeight}
                  onChange={(e) => handleDimensionChange("height", e.target.value)}
                  className="w-13 h-5 px-1 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded text-center focus:border-amber-500 focus:outline-none"
                  title="Height in millimeters"
                />
                <span className="text-[10px] text-slate-400">mm</span>
              </div>

              {/* Padding / Margin (mm) */}
              <div className="flex items-center gap-1 text-[11px] text-slate-600 font-medium">
                <span title="Margin padding in millimeters">Pad:</span>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={paperPadding}
                  onChange={(e) => handleDimensionChange("padding", e.target.value)}
                  className="w-10 h-5 px-1 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded text-center focus:border-amber-500 focus:outline-none"
                  title="Margin padding in millimeters"
                />
                <span className="text-[10px] text-slate-400">mm</span>
              </div>

              {/* Quick Roll Expander (+15mm / +30mm) */}
              <div className="flex items-center gap-0.5 ml-0.5">
                <button
                  type="button"
                  onClick={() => handleExtendHeight(15)}
                  className="px-1.5 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-[10px] font-bold cursor-pointer transition active:scale-95"
                  title="Extend roll length by +15 mm"
                >
                  +15
                </button>
                <button
                  type="button"
                  onClick={() => handleExtendHeight(30)}
                  className="px-1.5 py-0.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded text-[10px] font-bold cursor-pointer transition active:scale-95"
                  title="Extend roll length by +30 mm"
                >
                  +30
                </button>
              </div>

            </div>

            {/* Print Model Assigner Hub CTA */}
            <button
              type="button"
              onClick={() => setShowAssignerModal(true)}
              className="flex items-center gap-1.5 px-3 py-1 rounded bg-gradient-to-r from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 text-xs font-bold border border-emerald-300 text-emerald-900 transition shadow-2xs cursor-pointer active:scale-95"
              title="Assign print models to ERP voucher workflows and branch locations"
            >
              <FiPrinter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Assign Models</span>
              {assignments.length > 0 && (
                <span className="text-[9.5px] bg-emerald-600 text-white px-1.5 py-0.2 rounded-full font-bold">
                  {assignments.length}
                </span>
              )}
            </button>

            {/* Preset Gallery Showcase CTA */}
            <button
              type="button"
              onClick={() => setShowPresetGallery(true)}
              className="flex items-center gap-1.5 px-3 py-1 rounded bg-gradient-to-r from-amber-50 to-amber-100 hover:from-amber-100 hover:to-amber-200 text-xs font-bold border border-amber-300 text-amber-900 transition shadow-2xs cursor-pointer active:scale-95"
              title="Open full visual jewellery preset gallery"
            >
              <FiLayout className="w-3.5 h-3.5 text-amber-600" />
              <span>Preset Gallery</span>
              <span className="text-[9.5px] bg-amber-600 text-white px-1.5 py-0.2 rounded-full font-bold">{PRESET_GALLERY_ITEMS.length}</span>
            </button>

            {/* In-App Interactive User Manual / Guide Button */}
            <button
              type="button"
              onClick={() => setShowManualModal(true)}
              className="flex items-center gap-1.5 px-3 py-1 rounded bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-xs font-bold border border-purple-300 text-purple-900 transition shadow-2xs cursor-pointer active:scale-95"
              title="Open comprehensive User Manual & Guide for Invoice Designer and Model Assigner"
            >
              <FiHelpCircle className="w-3.5 h-3.5 text-purple-600" />
              <span>Guide / Manual</span>
            </button>

            {/* + Create New Template Button */}
            <button
              type="button"
              onClick={handleCreateNew}
              className="flex items-center gap-1 px-2.5 py-1 rounded bg-blue-50 hover:bg-blue-100 text-xs font-bold border border-blue-200 text-blue-700 transition shadow-2xs cursor-pointer active:scale-95"
              title="Create a fresh template with custom name"
            >
              <FiPlus className="w-3.5 h-3.5 text-blue-600" />
              New
            </button>

            {/* Test Render PDF Button */}
            <button
              type="button"
              onClick={handleTestRenderPdf}
              className="flex items-center gap-1 px-2.5 py-1 rounded bg-sky-50 hover:bg-sky-100 text-xs font-semibold border border-sky-200 text-sky-700 transition shadow-2xs cursor-pointer active:scale-95"
              title="Compile and preview live PDF with simulated data"
            >
              <FiEye className="w-3.5 h-3.5 text-sky-600" />
              Preview PDF
            </button>

            {/* Primary Save Template CTA */}
            {(hasSavePermission || hasEditPermission) && (
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1 rounded bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-bold text-xs shadow-2xs transition cursor-pointer active:scale-95 border border-amber-600 disabled:opacity-50"
              >
                <FiSave className="w-3.5 h-3.5 text-white" />
                {isSaving ? "Saving..." : "Save Template"}
              </button>
            )}

          </div>
        </div>

        {/* ── 3. Studio Workspace (3-Column Layout) ────────────────────────────── */}
        <div className="flex-1 flex overflow-hidden bg-slate-100 min-h-0">

          {/* ── LEFT PANEL: Templates Manager & Component Toolbox ── */}
          <aside className="w-68 bg-white border-r border-slate-200 flex flex-col shrink-0 overflow-hidden select-none">
            
            {/* Panel Mode Switcher Tabs (Templates vs Toolbox) */}
            <div className="flex border-b border-slate-200 bg-slate-50 shrink-0">
              <button
                type="button"
                onClick={() => setActiveLeftTab("templates")}
                className={`flex-1 py-1.5 px-2 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer border-b-2 ${
                  activeLeftTab === "templates"
                    ? "border-amber-500 text-amber-900 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
              >
                <FiAlignLeft className="w-3.5 h-3.5 text-amber-600" />
                <span>Templates</span>
                <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.2 rounded-full font-bold">
                  {savedTemplates.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveLeftTab("toolbox")}
                className={`flex-1 py-1.5 px-2 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer border-b-2 ${
                  activeLeftTab === "toolbox"
                    ? "border-amber-500 text-amber-900 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
              >
                <FiSliders className="w-3.5 h-3.5 text-amber-600" />
                <span>Toolbox</span>
              </button>
            </div>

            {/* TAB 1: Registered Templates & Presets (SmsSettings pattern) */}
            {activeLeftTab === "templates" && (
              <div className="flex-1 flex flex-col overflow-y-auto studio-scrollbar p-2.5 space-y-2">
                
                {/* + New Template Action Button */}
                <button
                  type="button"
                  onClick={handleCreateNew}
                  className={`w-full h-7 px-2 rounded-md border text-xs font-bold transition flex items-center justify-center space-x-1 cursor-pointer shadow-2xs ${
                    selectedTemplateId === "__NEW__"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  }`}
                >
                  <FiPlus className="w-3.5 h-3.5" />
                  <span>Create New Template</span>
                </button>

                {/* Section: Saved Templates */}
                <div>
                  <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-0.5">
                    <span>Saved Templates</span>
                    <span>{savedTemplates.length} Available</span>
                  </div>

                  <div className="space-y-1 max-h-[300px] overflow-y-auto pr-0.5 studio-scrollbar">
                    {isLoadingTemplates ? (
                      <div className="p-3 text-center text-xs text-slate-400">Loading saved templates...</div>
                    ) : savedTemplates.length === 0 ? (
                      <div className="p-3 text-center text-xs text-slate-400 bg-slate-50 rounded border border-dashed border-slate-200">
                        No saved templates yet. Click "+ Create New Template" to begin.
                      </div>
                    ) : (
                      savedTemplates.map((item) => {
                        const isSelected = selectedTemplateId === item.id;
                        return (
                          <div
                            key={item.id}
                            onClick={() => handleSelectTemplate(item)}
                            className={`w-full text-left p-1.5 rounded-md border transition flex items-center justify-between cursor-pointer group ${
                              isSelected
                                ? "bg-amber-50/90 border-amber-400 text-slate-900 shadow-2xs"
                                : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700"
                            }`}
                          >
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <span
                                className={`w-2 h-2 rounded-full shrink-0 ${
                                  isSelected ? "bg-emerald-500 ring-2 ring-emerald-300" : "bg-emerald-400"
                                }`}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] truncate font-bold text-slate-800 leading-tight">
                                  {item.templateName}
                                </div>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[8.5px] bg-sky-100 text-sky-800 px-1 rounded font-semibold shrink-0">
                                    {item.voucherType || "VOUCHER"}
                                  </span>
                                  <span className="text-[8.5px] text-slate-500 font-mono">
                                    {item.widthMm || 57}×{item.heightMm || 130}mm
                                  </span>
                                  {assignments.some((a) => Number(a.TemplateId) === Number(item.id)) && (
                                    <span
                                      className="text-[8px] bg-emerald-100 text-emerald-800 font-bold px-1 rounded border border-emerald-200 shrink-0"
                                      title="Active default print model for an assigned voucher"
                                    >
                                      ACTIVE
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Delete custom template button */}
                            {savedTemplates.length > 1 && hasDeletePermission && (
                              <button
                                type="button"
                                onClick={(e) => handleDeleteTemplate(item, e)}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition cursor-pointer"
                                title="Delete template"
                              >
                                <FiTrash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Section: Standard Preset Starters Showcase Banner */}
                <div className="pt-2 border-t border-slate-200 mt-2">
                  <div className="p-2.5 rounded-xl border border-amber-200/90 bg-gradient-to-br from-amber-50/80 via-white to-amber-100/40 shadow-xs flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-amber-600 to-amber-400 text-white flex items-center justify-center text-[10px] font-bold shadow-xs">
                          ★
                        </div>
                        <span className="text-[11px] font-bold text-slate-800" style={INTER}>
                          Jewellery Presets
                        </span>
                      </div>
                      <span className="text-[9px] font-bold text-amber-800 bg-amber-100/90 px-1.5 py-0.5 rounded-full border border-amber-200">
                        {PRESET_GALLERY_ITEMS.length} Layouts
                      </span>
                    </div>

                    <p className="text-[10px] text-slate-600 leading-tight">
                      Production layouts for GST sales, old gold vouchers, receipts, payments &amp; tags.
                    </p>

                    <button
                      type="button"
                      onClick={() => setShowPresetGallery(true)}
                      className="w-full py-1.5 px-2 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white rounded-lg text-[11px] font-bold shadow-xs flex items-center justify-center gap-1.5 transition active:scale-95 cursor-pointer"
                    >
                      <FiLayout className="w-3.5 h-3.5" />
                      <span>Browse Preset Gallery</span>
                    </button>

                    {/* Quick Access Popular Chips */}
                    <div className="flex items-center gap-1 pt-1 border-t border-amber-200/60 text-[9.5px]">
                      <span className="text-slate-400 text-[9px] font-medium shrink-0">Quick:</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId("__NEW__");
                          loadTemplate(SAMPLE_TEMPLATES.a4Sales.template, SAMPLE_TEMPLATES.a4Sales.name, "A4", "SALE INVOICE");
                        }}
                        className="px-1.5 py-0.5 bg-white hover:bg-sky-50 text-sky-800 border border-slate-200 hover:border-sky-300 rounded font-semibold transition cursor-pointer truncate"
                        title="Load A4 Luxury Tax Invoice"
                      >
                        A4 Sales
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId("__NEW__");
                          loadTemplate(SAMPLE_TEMPLATES.a4Purchase.template, SAMPLE_TEMPLATES.a4Purchase.name, "A4", "PURCHASE VOUCHER");
                        }}
                        className="px-1.5 py-0.5 bg-white hover:bg-emerald-50 text-emerald-800 border border-slate-200 hover:border-emerald-300 rounded font-semibold transition cursor-pointer truncate"
                        title="Load A4 Old Gold Voucher"
                      >
                        A4 Old Gold
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplateId("__NEW__");
                          loadTemplate(SAMPLE_TEMPLATES.thermal80mmSale.template, SAMPLE_TEMPLATES.thermal80mmSale.name, "80MM", "SALE INVOICE");
                        }}
                        className="px-1.5 py-0.5 bg-white hover:bg-slate-100 text-slate-800 border border-slate-200 hover:border-slate-300 rounded font-semibold transition cursor-pointer truncate"
                        title="Load 80mm POS Thermal Receipt"
                      >
                        80mm
                      </button>
                    </div>
                  </div>
                </div>

                {/* JSON Export Button */}
                <div className="pt-2 border-t border-slate-200 mt-auto">
                  <button
                    type="button"
                    onClick={handleExportJson}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded text-xs text-slate-600 font-medium transition cursor-pointer"
                    title="Download template schema as JSON"
                  >
                    <FiDownload className="w-3.5 h-3.5 text-slate-500" />
                    Export Template JSON
                  </button>
                </div>

              </div>
            )}

            {/* TAB 2: Component Toolbox (Print Primitives & Modules) */}
            {activeLeftTab === "toolbox" && (
              <div className="flex-1 flex flex-col overflow-y-auto studio-scrollbar p-3 space-y-4 text-xs">
                
                {/* Print Primitives */}
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2" style={INTER}>
                    Print Primitives (Click to add)
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    
                    {/* Static Text */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("text", { content: "Header Title" })}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-amber-50/50 hover:border-amber-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                    >
                      <FiType className="text-amber-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Static Text</span>
                    </button>

                    {/* Token Text */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("text", { content: "Customer: {{CustomerName}}" })}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-sky-50/50 hover:border-sky-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                    >
                      <FiCode className="text-sky-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Token Text</span>
                    </button>

                    {/* Barcode 128 */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("code128")}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-indigo-50/50 hover:border-indigo-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                    >
                      <FiMaximize className="text-indigo-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Barcode 128</span>
                    </button>

                    {/* Dynamic QR */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("qrcode")}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-emerald-50/50 hover:border-emerald-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                    >
                      <FiGrid className="text-emerald-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Dynamic QR</span>
                    </button>

                    {/* Logo / Image Uploader */}
                    <button
                      type="button"
                      onClick={() => logoFileInputRef.current?.click()}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-rose-50/50 hover:border-rose-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                      title="Upload and insert brand logo PNG/JPG"
                    >
                      <FiImage className="text-rose-500 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Store Logo</span>
                    </button>

                    {/* Divider Line */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("line")}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-amber-50/50 hover:border-amber-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                    >
                      <FiMinus className="text-slate-500 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">Divider Line</span>
                    </button>

                    {/* 10-Col Table */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("table")}
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-blue-50/50 hover:border-blue-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                      title="Add 10-Column Jewelry Particulars Table"
                    >
                      <FiTable className="text-blue-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">10-Col Table</span>
                    </button>

                    {/* 5-Col Table */}
                    <button
                      type="button"
                      onClick={() =>
                        handleAddPrimitive("table", {
                          name: `table_5col_${Date.now().toString().slice(-4)}`,
                          head: ["Sl", "Item Particulars", "Qty", "Rate (₹)", "Amount (₹)"],
                          headWidthPercentages: [8, 42, 12, 18, 20],
                          content: JSON.stringify([
                            ["1", "Gold Ornament / Bullion", "1", "6,950.00", "6,950.00"]
                          ])
                        })
                      }
                      className="flex flex-col items-center justify-center p-2 rounded border border-slate-200 bg-white hover:bg-teal-50/50 hover:border-teal-400 text-slate-700 transition shadow-2xs group cursor-pointer"
                      title="Add 5-Column Standard Table"
                    >
                      <FiTable className="text-teal-600 group-hover:scale-110 transition text-lg" />
                      <span className="text-[11px] font-medium mt-1">5-Col Table</span>
                    </button>

                    {/* Custom Table Dialog */}
                    <button
                      type="button"
                      onClick={handlePromptCustomTable}
                      className="col-span-2 flex items-center justify-center gap-1.5 p-1.5 rounded border border-dashed border-amber-300 bg-amber-50/60 hover:bg-amber-100/70 text-amber-800 transition shadow-2xs font-semibold cursor-pointer"
                      title="Customize column count, headers, and height"
                    >
                      <FiPlus className="text-amber-600 w-3.5 h-3.5" />
                      <span>Custom Table Builder...</span>
                    </button>

                  </div>
                </div>

                {/* ── Section: Logos & QR Codes ── */}
                <div className="pt-2 border-t border-slate-200">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2" style={INTER}>
                    Logos &amp; QR Codes
                  </span>
                  <div className="space-y-1.5">
                    {/* Upload Store Logo */}
                    <button
                      type="button"
                      onClick={() => logoFileInputRef.current?.click()}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-rose-50/40 border border-slate-200 hover:border-rose-400 text-left transition group shadow-2xs cursor-pointer"
                      title="Upload and insert showroom logo image (PNG/JPG)"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-rose-100 text-rose-700">
                          <FiImage className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">Upload Store Logo</div>
                          <div className="text-[9px] text-slate-500">Insert PNG, JPG, or SVG emblem</div>
                        </div>
                      </div>
                      <span className="text-rose-600 text-xs font-bold">+</span>
                    </button>

                    {/* Dynamic UPI Payment QR */}
                    <button
                      type="button"
                      onClick={handlePromptUpiQr}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-emerald-50/40 border border-slate-200 hover:border-emerald-400 text-left transition group shadow-2xs cursor-pointer"
                      title="Configure and insert scanner-ready UPI QR code"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-emerald-100 text-emerald-700">
                          <FiGrid className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">UPI Payment QR Code</div>
                          <div className="text-[9px] text-slate-500">Scan &amp; Pay (Opens account directly)</div>
                        </div>
                      </div>
                      <span className="text-emerald-600 text-xs font-bold">+</span>
                    </button>

                    {/* Verification / Web QR */}
                    <button
                      type="button"
                      onClick={() =>
                        handleAddPrimitive("qrcode", {
                          name: `web_qr_${Date.now().toString().slice(-4)}`,
                          content: "https://shergold.com/verify?inv={{InvoiceNo}}",
                          width: 20,
                          height: 20
                        })
                      }
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-sky-50/40 border border-slate-200 hover:border-sky-400 text-left transition group shadow-2xs cursor-pointer"
                      title="Insert online e-invoice verification QR"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-sky-100 text-sky-700">
                          <FiGrid className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">Website / Verify QR</div>
                          <div className="text-[9px] text-slate-500">Online bill verification link</div>
                        </div>
                      </div>
                      <span className="text-sky-600 text-xs font-bold">+</span>
                    </button>
                  </div>
                </div>

                {/* Jewelry ERP Modules */}
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2" style={INTER}>
                    Jewelry ERP Modules
                  </span>
                  <div className="space-y-1.5">

                    {/* 10-Column Jewelry Particulars Table Module */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("table")}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-blue-50/40 border border-slate-200 hover:border-blue-400 text-left transition group shadow-2xs cursor-pointer"
                      title="Insert 10-Column Jewelry Particulars Table"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-blue-100 text-blue-700">
                          <FiTable className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">10-Col Jewelry Table</div>
                          <div className="text-[9px] text-slate-500">Sl, Code, Wts, Purity, VA%, Rate, Total</div>
                        </div>
                      </div>
                      <span className="text-blue-600 text-xs font-bold">+</span>
                    </button>

                    {/* Bullion Summary Table */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("rectangle", {
                        name: "bullion_rate_box",
                        width: Math.max(10, paperWidth - 8),
                        height: 14,
                        borderWidth: 0.5,
                        borderColor: "#d97706"
                      })}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-amber-50/40 border border-slate-200 hover:border-amber-400 text-left transition group shadow-2xs cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-amber-100 text-amber-700">
                          <FiCreditCard className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">Bullion Rates Strip</div>
                          <div className="text-[9px] text-slate-500">22K / 24K Live Daily Rates</div>
                        </div>
                      </div>
                      <span className="text-amber-600 text-xs font-bold">+</span>
                    </button>

                    {/* Scheme Passbook Box */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("rectangle", {
                        name: "passbook_summary_box",
                        width: Math.max(10, paperWidth - 8),
                        height: 22,
                        borderWidth: 0.5,
                        borderColor: "#0284c7"
                      })}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-sky-50/40 border border-slate-200 hover:border-sky-400 text-left transition group shadow-2xs cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-sky-100 text-sky-700">
                          <FiAward className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">Scheme Passbook Box</div>
                          <div className="text-[9px] text-slate-500">Installment &amp; Balances</div>
                        </div>
                      </div>
                      <span className="text-sky-600 text-xs font-bold">+</span>
                    </button>

                    {/* BIS Hallmark & HUID Box */}
                    <button
                      type="button"
                      onClick={() => handleAddPrimitive("text", {
                        name: "bis_hallmark_box",
                        width: Math.min(paperWidth - 8, 45),
                        height: 10,
                        content: "BIS 916 HALLMARKED\nHUID: {{HUID}}",
                        fontSize: 8,
                        alignment: "center"
                      })}
                      className="w-full flex items-center justify-between p-2 rounded bg-white hover:bg-emerald-50/40 border border-slate-200 hover:border-emerald-400 text-left transition group shadow-2xs cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="p-1 rounded bg-emerald-100 text-emerald-700">
                          <FiCheckCircle className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-[11px] font-bold text-slate-800">BIS Hallmark &amp; HUID</div>
                          <div className="text-[9px] text-slate-500">6-char laser token</div>
                        </div>
                      </div>
                      <span className="text-emerald-600 text-xs font-bold">+</span>
                    </button>

                  </div>
                </div>

              </div>
            )}

            {/* Bottom Status Pin */}
            <div className="mt-auto p-2 border-t border-slate-200 bg-slate-50 text-xs shrink-0">
              <div className="flex items-center justify-between text-slate-500 text-[10.5px]">
                <span>Active Spec:</span>
                <span className="font-mono text-slate-800 font-bold">{selectedPaperId}</span>
              </div>
              <div className="flex items-center justify-between text-slate-500 text-[10.5px] mt-0.5">
                <span>Dimensions:</span>
                <span className="font-mono text-amber-700 font-semibold">
                  {paperWidth} × {paperHeight} mm
                </span>
              </div>
            </div>

          </aside>

          {/* ── CENTER PANEL: Visual Canvas Viewport ── */}
          <main className="flex-1 flex flex-col bg-slate-100 overflow-hidden relative min-h-0">
            
            {/* Viewport Telemetry Bar */}
            <div className="h-8 bg-white border-b border-slate-200 px-3 flex items-center justify-between text-xs shrink-0 select-none z-10 shadow-2xs">
              
              {/* Enhanced Zoom Controls */}
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1 bg-slate-100 rounded p-0.5 border border-slate-200 text-[11px]">
                  {/* Step Zoom Out Button */}
                  <button
                    type="button"
                    onClick={handleZoomOut}
                    disabled={parseFloat(zoomLevel) <= 25}
                    className="p-1 rounded hover:bg-white text-slate-600 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer transition"
                    title="Zoom Out (Ctrl + -)"
                  >
                    <FiMinus className="w-3 h-3" />
                  </button>

                  {/* Preset Quick Chips */}
                  {["50%", "75%", "100%", "150%", "200%", "300%", "400%"].map((z) => (
                    <button
                      key={z}
                      type="button"
                      onClick={() => setZoomLevel(z)}
                      className={`px-1.5 py-0.5 rounded cursor-pointer transition font-medium ${
                        zoomLevel === z
                          ? "bg-amber-500 text-white font-bold shadow-2xs"
                          : "text-slate-600 hover:bg-white"
                      }`}
                    >
                      {z}
                    </button>
                  ))}

                  {/* Step Zoom In Button */}
                  <button
                    type="button"
                    onClick={handleZoomIn}
                    disabled={parseFloat(zoomLevel) >= 400}
                    className="p-1 rounded hover:bg-white text-slate-600 hover:text-amber-700 disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer transition"
                    title="Zoom In (Ctrl + +)"
                  >
                    <FiPlus className="w-3 h-3" />
                  </button>

                  {/* Quick 1:1 Reset Button */}
                  {zoomLevel !== "100%" && (
                    <button
                      type="button"
                      onClick={handleZoomReset}
                      className="px-1.5 py-0.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 font-bold text-[10px] cursor-pointer transition shadow-2xs"
                      title="Reset Zoom to 100% (Ctrl + 0)"
                    >
                      1:1
                    </button>
                  )}
                </div>
                {/* Faint Canvas Overlay Grid Quick-Toggle */}
                <button
                  type="button"
                  onClick={() => setShowCanvasOverlayGrid((prev) => !prev)}
                  className={`flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold transition cursor-pointer border ${
                    showCanvasOverlayGrid
                      ? "bg-blue-600 text-white border-blue-600 shadow-2xs"
                      : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"
                  }`}
                  title="Toggle faint millimeter alignment grid, center crosshairs & printable margin bounds directly above canvas"
                >
                  <FiGrid className="w-3.5 h-3.5" />
                  <span>{showCanvasOverlayGrid ? "Overlay Grid: ON" : "Overlay Grid: OFF"}</span>
                </button>

                <span className="text-[10px] text-slate-400 font-medium">Visual Drafting Workspace</span>
              </div>

              {/* In-Studio Feedback Pill */}
              {studioToast && (
                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-300 text-[11px] font-semibold animate-pulse shadow-2xs">
                  <FiCheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{studioToast}</span>
                </div>
              )}

              {/* Live Coordinates Telemetry */}
              <div className="flex items-center space-x-3 font-mono text-[10.5px] text-slate-500 bg-slate-50 px-2.5 py-0.5 rounded border border-slate-200">
                <span>Page: <strong className="text-slate-800">{paperWidth}×{paperHeight}mm</strong></span>
                <span className="text-slate-300">|</span>
                <span>X: <strong className="text-amber-600">{activeTelemetry.x} mm</strong></span>
                <span>Y: <strong className="text-amber-600">{activeTelemetry.y} mm</strong></span>
                <span className="text-slate-300">|</span>
                <span>W: <strong className="text-slate-700">{activeTelemetry.width} mm</strong></span>
                <span>H: <strong className="text-slate-700">{activeTelemetry.height} mm</strong></span>
              </div>

            </div>

            {/* Canvas Host: Embeds @pdfme/ui Designer */}
            <div
              className={`flex-1 overflow-auto studio-scrollbar relative ${snapGridEnabled ? "bg-slate-100" : "light-drafting-grid"}`}
              style={
                snapGridEnabled
                  ? {
                      backgroundImage: "radial-gradient(#94a3b8 1.1px, transparent 1.1px)",
                      backgroundSize: `${gridSize * 3.78}px ${gridSize * 3.78}px`
                    }
                  : undefined
              }
            >
              <div
                ref={designerContainerRef}
                className={`pdfme-designer-host ${
                  showCanvasOverlayGrid ? `show-paper-grid grid-density-${overlayGridSpacing}mm` : ""
                }`}
                style={{
                  transform: `scale(${parseFloat(zoomLevel) / 100})`,
                  transformOrigin: "top center",
                  transition: "transform 0.15s ease-out",
                  marginBottom: parseFloat(zoomLevel) > 100 ? `${Math.round((parseFloat(zoomLevel) / 100 - 1) * 100)}%` : "0px",
                  paddingBottom: parseFloat(zoomLevel) > 100 ? `${Math.round((parseFloat(zoomLevel) / 100 - 1) * 250)}px` : "0px",
                  "--paper-margin-px": `${(paperPadding || 8) * 3.7795}px`
                }}
              />
              {/* Hidden file input for logo uploads */}
              <input
                type="file"
                ref={logoFileInputRef}
                accept="image/*"
                onChange={handleLogoFileUpload}
                style={{ display: "none" }}
              />
            </div>

          </main>

          {/* ── RIGHT PANEL: Dynamic Variables & Properties Inspector ── */}
          <aside className="w-72 bg-white border-l border-slate-200 flex flex-col shrink-0 overflow-hidden select-none">
            
            {/* Inspector Tab Switcher */}
            <div className="flex border-b border-slate-200 bg-slate-50 shrink-0">
              <button
                type="button"
                onClick={() => setActiveInspectorTab("tokens")}
                className={`flex-1 py-2 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer border-b-2 ${
                  activeInspectorTab === "tokens"
                    ? "border-amber-500 text-amber-900 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
                style={INTER}
              >
                <FiCode className="w-3.5 h-3.5 text-amber-600" />
                <span>Tokens</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveInspectorTab("symbols")}
                className={`flex-1 py-2 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer border-b-2 ${
                  activeInspectorTab === "symbols"
                    ? "border-amber-500 text-amber-900 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
                style={INTER}
              >
                <span className="font-bold text-amber-600 text-xs">₹</span>
                <span>Symbols &amp; Coins</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveInspectorTab("styles")}
                className={`flex-1 py-2 text-xs font-bold transition flex items-center justify-center gap-1.5 cursor-pointer border-b-2 ${
                  activeInspectorTab === "styles"
                    ? "border-amber-500 text-amber-900 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                }`}
                style={INTER}
              >
                <FiInfo className="w-3.5 h-3.5 text-amber-600" />
                <span>Inspector</span>
              </button>
            </div>

            {/* TAB 1: Dynamic System Variables Palette */}
            {activeInspectorTab === "tokens" && (
              <div className="flex-1 flex flex-col overflow-hidden">
                
                {/* Search Box */}
                <div className="p-2 border-b border-slate-200 bg-slate-50 shrink-0">
                  <div className="relative flex items-center">
                    <FiSearch className="absolute left-2.5 text-slate-400 w-3.5 h-3.5" />
                    <input
                      type="text"
                      value={tokenSearch}
                      onChange={(e) => setTokenSearch(e.target.value)}
                      placeholder="Search tokens (e.g. rate, gold, name)..."
                      className="w-full bg-white border border-slate-300 rounded pl-8 pr-2.5 py-1 text-xs text-slate-800 focus:outline-none focus:border-amber-500"
                      style={INTER}
                    />
                    {tokenSearch && (
                      <button
                        type="button"
                        onClick={() => setTokenSearch("")}
                        className="absolute right-2 text-slate-400 hover:text-slate-600"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Tokens Categorized List */}
                <div className="flex-1 overflow-y-auto p-2.5 space-y-3 studio-scrollbar">
                  {filteredCategories.map((cat) => {
                    const IconComponent = CATEGORY_ICONS[cat.id] || FiTag;
                    return (
                      <div key={cat.id} className="space-y-1">
                        
                        <div className="flex items-center gap-1.5 text-slate-700 pb-1 border-b border-slate-100">
                          <IconComponent className="w-3.5 h-3.5 text-amber-600" />
                          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700" style={INTER}>
                            {cat.name}
                          </span>
                          <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded ml-auto font-mono">
                            {cat.tokens.length}
                          </span>
                        </div>

                        <div className="space-y-1 pt-1">
                          {cat.tokens.map((token) => (
                            <div
                              key={token.key}
                              className="group p-1.5 rounded border border-slate-200 bg-white hover:border-amber-400 hover:bg-amber-50/30 transition shadow-2xs flex items-center justify-between"
                            >
                              <div className="min-w-0 flex-1 pr-1">
                                <div className="flex items-center gap-1">
                                  <span className="text-[11px] font-bold text-slate-800 truncate" style={INTER}>
                                    {token.label}
                                  </span>
                                  {token.example && (
                                    <span className="text-[9px] text-slate-400 font-mono truncate max-w-[80px]">
                                      ≈ {token.example}
                                    </span>
                                  )}
                                </div>
                                <div className="text-[9.5px] font-mono text-amber-800 bg-amber-50/80 px-1 py-0.2 rounded inline-block mt-0.5 select-all">
                                  {token.key}
                                </div>
                              </div>

                              <div className="flex items-center gap-1 shrink-0">
                                {/* Insert directly to canvas */}
                                <button
                                  type="button"
                                  onClick={() => handleInsertToken(token)}
                                  className="p-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition cursor-pointer"
                                  title="Add token field to canvas"
                                >
                                  <FiPlus className="w-3 h-3" />
                                </button>

                                {/* Copy token syntax */}
                                <button
                                  type="button"
                                  onClick={() => handleCopyToken(token.key)}
                                  className={`p-1 rounded border transition cursor-pointer ${
                                    copiedToken === token.key
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                      : "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                                  }`}
                                  title="Copy token key to clipboard"
                                >
                                  {copiedToken === token.key ? (
                                    <FiCheck className="w-3 h-3 text-emerald-600" />
                                  ) : (
                                    <FiCopy className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                      </div>
                    );
                  })}
                </div>

                {/* Footer Guide */}
                <div className="p-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 shrink-0">
                  <div className="flex items-center gap-1 font-semibold text-slate-700">
                    <FiInfo className="w-3 h-3 text-amber-600" />
                    <span>How tokens work:</span>
                  </div>
                  <p className="mt-0.5 leading-tight">
                    Double-click any text field on the canvas to paste dynamic tokens like{" "}
                    <code className="text-amber-700 font-mono font-bold">{"{{CustomerName}}"}</code>.
                  </p>
                </div>

              </div>
            )}

            {/* TAB 2: Symbols & Coins Palette */}
            {activeInspectorTab === "symbols" && (
              <div className="flex-1 flex flex-col overflow-hidden">
                
                {/* Search Box */}
                <div className="p-2 border-b border-slate-200 bg-slate-50 shrink-0">
                  <div className="relative flex items-center">
                    <FiSearch className="absolute left-2.5 text-slate-400 w-3.5 h-3.5" />
                    <input
                      type="text"
                      value={symbolSearch}
                      onChange={(e) => setSymbolSearch(e.target.value)}
                      placeholder="Search symbols (e.g. rupee, coin, gold, 916)..."
                      className="w-full bg-white border border-slate-300 rounded pl-8 pr-2.5 py-1 text-xs text-slate-800 focus:outline-none focus:border-amber-500"
                      style={INTER}
                    />
                    {symbolSearch && (
                      <button
                        type="button"
                        onClick={() => setSymbolSearch("")}
                        className="absolute right-2 text-slate-400 hover:text-slate-600"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Popular Quick Symbols Strip inside Right Sidebar */}
                <div className="px-2.5 py-2 border-b border-slate-200 bg-amber-50/50 shrink-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold text-amber-900 uppercase tracking-wider flex items-center gap-1">
                      <span className="text-amber-700 font-extrabold text-xs">₹</span>
                      <span>Quick Pick Symbols</span>
                    </span>
                    <span className="text-[9px] text-amber-700/80 font-medium">Click to Copy / Add</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {POPULAR_QUICK_SYMBOLS.map((item) => {
                      const isCopied = copiedSymbol === item.symbol;
                      return (
                        <button
                          key={item.symbol}
                          type="button"
                          onClick={() => handleCopySymbol(item.symbol)}
                          onDoubleClick={() => handleInsertSymbol(item.symbol)}
                          className={`px-1.5 py-0.5 rounded text-[11px] font-bold font-mono transition cursor-pointer border ${
                            isCopied
                              ? "bg-emerald-600 text-white border-emerald-600 shadow-2xs"
                              : "bg-white hover:bg-amber-100 text-slate-800 border-slate-200 hover:border-amber-300"
                          }`}
                          title={`${item.label} — ${item.hint}\n• Click: Copy to clipboard (Ctrl+V)\n• Double-click: Add to canvas`}
                        >
                          {item.symbol}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Symbols Categorized List */}
                <div className="flex-1 overflow-y-auto p-2.5 space-y-3 studio-scrollbar">
                  {filteredSymbolCategories.map((cat) => (
                    <div key={cat.id} className="space-y-1">
                      
                      <div className="flex items-center gap-1.5 text-slate-700 pb-1 border-b border-slate-100">
                        <span className="w-4 h-4 rounded bg-amber-100 text-amber-800 flex items-center justify-center font-bold text-[11px] leading-none shrink-0">
                          {cat.badge}
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700" style={INTER}>
                          {cat.name}
                        </span>
                        <span className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded ml-auto font-mono">
                          {cat.items.length}
                        </span>
                      </div>

                      <div className="space-y-1 pt-1">
                        {cat.items.map((item) => (
                          <div
                            key={item.code}
                            className="group p-1.5 rounded border border-slate-200 bg-white hover:border-amber-400 hover:bg-amber-50/30 transition shadow-2xs flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1 pr-1">
                              {/* Big Symbol Badge */}
                              <button
                                type="button"
                                onClick={() => handleCopySymbol(item.symbol)}
                                className="w-7 h-7 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold font-mono text-sm flex items-center justify-center shrink-0 cursor-pointer transition active:scale-95 shadow-2xs"
                                title={`Click to copy "${item.symbol}" to clipboard`}
                              >
                                {item.symbol}
                              </button>

                              <div className="min-w-0 flex-1">
                                <div className="text-[11px] font-bold text-slate-800 truncate" style={INTER}>
                                  {item.label}
                                </div>
                                <div className="text-[9.5px] text-slate-500 truncate leading-tight">
                                  {item.desc}
                                </div>
                                {item.sampleSnippet && item.sampleSnippet !== item.symbol && (
                                  <div className="text-[9px] font-mono text-amber-800 bg-amber-50/80 px-1 py-0.2 rounded inline-block mt-0.5 select-all">
                                    {item.sampleSnippet}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              {/* Insert / Append to canvas */}
                              <button
                                type="button"
                                onClick={() => handleInsertSymbol(item)}
                                className="px-1.5 py-1 rounded bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold flex items-center gap-0.5 transition cursor-pointer"
                                title="Add as text element to canvas or append to selected"
                              >
                                <FiPlus className="w-3 h-3" />
                                <span>Canvas</span>
                              </button>

                              {/* Copy symbol */}
                              <button
                                type="button"
                                onClick={() => handleCopySymbol(item.symbol)}
                                className={`p-1 rounded border transition cursor-pointer ${
                                  copiedSymbol === item.symbol
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                                    : "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                                }`}
                                title="Copy symbol to clipboard (Ctrl+V anywhere)"
                              >
                                {copiedSymbol === item.symbol ? (
                                  <FiCheck className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                  <FiCopy className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>
                  ))}
                </div>

                {/* Footer Guide */}
                <div className="p-2 border-t border-slate-200 bg-slate-50 text-[10px] text-slate-500 shrink-0">
                  <div className="flex items-center gap-1 font-semibold text-slate-700">
                    <FiInfo className="w-3 h-3 text-amber-600" />
                    <span>How print symbols work:</span>
                  </div>
                  <p className="mt-0.5 leading-tight">
                    Click any symbol or <code className="text-amber-700 font-mono font-bold">Copy</code> to copy it to clipboard. Press <kbd className="bg-slate-200 px-1 rounded text-[9px] font-mono">Ctrl+V</kbd> inside text boxes or table headers.
                  </p>
                </div>

              </div>
            )}

            {/* TAB 3: Properties & Active Telemetry & Alignment Suite */}
            {activeInspectorTab === "styles" && (
              <div className="flex-1 overflow-y-auto p-3 space-y-3 studio-scrollbar text-xs">
                
                {/* PHASE 2: Multi-Element Alignment Studio (Visible when 2+ items selected) */}
                {selectedElementNames.length > 1 && (
                  <div className="bg-gradient-to-br from-amber-500/10 via-amber-50 to-orange-50 border border-amber-300 rounded p-2.5 space-y-2.5 shadow-2xs">
                    <div className="flex items-center justify-between pb-1 border-b border-amber-200">
                      <div className="flex items-center gap-1.5">
                        <FiLayers className="w-3.5 h-3.5 text-amber-700" />
                        <span className="font-bold text-amber-950 text-[11px] uppercase tracking-wider" style={INTER}>
                          Multi-Selection Suite
                        </span>
                      </div>
                      <span className="text-[10px] bg-amber-600 text-white font-mono font-bold px-1.5 py-0.2 rounded-full">
                        {selectedElementNames.length} Items
                      </span>
                    </div>

                    {/* Edge & Axis Alignment */}
                    <div className="space-y-1">
                      <span className="text-[9.5px] font-bold text-amber-900 uppercase tracking-wider block">
                        Align Relative Edges
                      </span>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          type="button"
                          onClick={() => handleMultiAlign("left")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Align all left edges with leftmost item"
                        >
                          <FiAlignLeft className="w-3 h-3 text-amber-600" />
                          <span>Lefts</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMultiAlign("centerH")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Center all items along common vertical axis"
                        >
                          <FiAlignCenter className="w-3 h-3 text-amber-600" />
                          <span>Center H</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMultiAlign("right")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Align all right edges with rightmost item"
                        >
                          <FiAlignRight className="w-3 h-3 text-amber-600" />
                          <span>Rights</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleMultiAlign("top")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Align all top edges with topmost item"
                        >
                          <FiArrowUp className="w-3 h-3 text-amber-600" />
                          <span>Tops</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMultiAlign("centerV")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Center all items along common horizontal axis"
                        >
                          <FiMove className="w-3 h-3 text-amber-600" />
                          <span>Center V</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMultiAlign("bottom")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Align all bottom edges with bottommost item"
                        >
                          <FiArrowDown className="w-3 h-3 text-amber-600" />
                          <span>Bottoms</span>
                        </button>
                      </div>
                    </div>

                    {/* Equal Spacing & Distribution */}
                    <div className="space-y-1 pt-1 border-t border-amber-200/70">
                      <span className="text-[9.5px] font-bold text-amber-900 uppercase tracking-wider block">
                        Even Spacing / Distribution
                      </span>
                      <div className="grid grid-cols-2 gap-1">
                        <button
                          type="button"
                          onClick={() => handleDistribute("vertical")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Make vertical gaps identical between stacked rows"
                        >
                          <FiDivide className="w-3 h-3 text-amber-600" />
                          <span>Equal Vertical</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDistribute("horizontal")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Make horizontal gaps identical between columns"
                        >
                          <FiColumns className="w-3 h-3 text-amber-600" />
                          <span>Equal Horiz</span>
                        </button>
                      </div>
                    </div>

                    {/* Size Harmonization */}
                    <div className="space-y-1 pt-1 border-t border-amber-200/70">
                      <span className="text-[9.5px] font-bold text-amber-900 uppercase tracking-wider block">
                        Match Dimensions
                      </span>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          type="button"
                          onClick={() => handleMatchSize("width-max")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Match width of all items to widest item"
                        >
                          <FiMaximize2 className="w-3 h-3 text-amber-600" />
                          <span>Width</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMatchSize("height-max")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Match height of all items to tallest item"
                        >
                          <FiMaximize2 className="w-3 h-3 text-amber-600 rotate-90" />
                          <span>Height</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMatchSize("square")}
                          className="flex items-center justify-center gap-1 px-1.5 py-1 bg-white hover:bg-amber-100 text-slate-800 border border-amber-200 rounded text-[10px] font-medium transition cursor-pointer"
                          title="Make all items square (equal width and height)"
                        >
                          <FiMinimize2 className="w-3 h-3 text-amber-600" />
                          <span>Square</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Layer Details */}
                <div className="bg-slate-50 border border-slate-200 rounded p-2.5 space-y-2.5">
                  <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                    <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider" style={INTER}>
                      Selected Element
                    </span>
                    <div className="flex items-center gap-1">
                      {activeTelemetry.type && (
                        <span className="text-[9.5px] bg-slate-200 text-slate-700 px-1.5 py-0.2 rounded font-mono font-semibold uppercase">
                          {activeTelemetry.type}
                        </span>
                      )}
                      <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono font-bold ${
                        activeTelemetry.name !== "None"
                          ? "bg-amber-100 text-amber-800"
                          : "bg-slate-200 text-slate-500"
                      }`}>
                        {activeTelemetry.name}
                      </span>
                    </div>
                  </div>

                  {activeTelemetry.name === "None" ? (
                    <div className="text-[11px] text-slate-500 italic text-center py-2 bg-white rounded border border-dashed border-slate-200">
                      Click any element on canvas to inspect and align
                    </div>
                  ) : (
                    <>
                      {/* Precise Geometry Inputs (mm) */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">
                            Exact Geometry (mm)
                          </span>
                          <span className="text-[9px] text-slate-400">Step: {nudgeStep}mm</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div className="bg-white p-1.5 rounded border border-slate-200 focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-200">
                            <span className="text-slate-400 block text-[9.5px]">X (Left)</span>
                            <div className="flex items-center">
                              <input
                                type="number"
                                step={nudgeStep}
                                min="0"
                                max={paperWidth}
                                value={activeTelemetry.x}
                                onChange={(e) => handleUpdateSelectedSchema({ x: e.target.value })}
                                className="w-full text-slate-800 font-mono font-bold outline-none text-xs bg-transparent"
                              />
                              <span className="text-[9px] text-slate-400 font-mono">mm</span>
                            </div>
                          </div>
                          <div className="bg-white p-1.5 rounded border border-slate-200 focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-200">
                            <span className="text-slate-400 block text-[9.5px]">Y (Top)</span>
                            <div className="flex items-center">
                              <input
                                type="number"
                                step={nudgeStep}
                                min="0"
                                max={paperHeight}
                                value={activeTelemetry.y}
                                onChange={(e) => handleUpdateSelectedSchema({ y: e.target.value })}
                                className="w-full text-slate-800 font-mono font-bold outline-none text-xs bg-transparent"
                              />
                              <span className="text-[9px] text-slate-400 font-mono">mm</span>
                            </div>
                          </div>
                          <div className="bg-white p-1.5 rounded border border-slate-200 focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-200">
                            <span className="text-slate-400 block text-[9.5px]">Width</span>
                            <div className="flex items-center">
                              <input
                                type="number"
                                step={nudgeStep}
                                min="1"
                                max={paperWidth}
                                value={activeTelemetry.width}
                                onChange={(e) => handleUpdateSelectedSchema({ width: e.target.value })}
                                className="w-full text-slate-800 font-mono font-bold outline-none text-xs bg-transparent"
                              />
                              <span className="text-[9px] text-slate-400 font-mono">mm</span>
                            </div>
                          </div>
                          <div className="bg-white p-1.5 rounded border border-slate-200 focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-200">
                            <span className="text-slate-400 block text-[9.5px]">Height</span>
                            <div className="flex items-center">
                              <input
                                type="number"
                                step={nudgeStep}
                                min="0.2"
                                max={paperHeight}
                                value={activeTelemetry.height}
                                onChange={(e) => handleUpdateSelectedSchema({ height: e.target.value })}
                                className="w-full text-slate-800 font-mono font-bold outline-none text-xs bg-transparent"
                              />
                              <span className="text-[9px] text-slate-400 font-mono">mm</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Page Alignment Suite */}
                      <div className="pt-1 border-t border-slate-200 space-y-1.5">
                        <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">
                          Page &amp; Margin Alignment
                        </span>
                        <div className="grid grid-cols-2 gap-1.5">
                          <button
                            type="button"
                            onClick={handleAlignPageCenterH}
                            className="flex items-center justify-center gap-1 px-2 py-1 bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-medium transition cursor-pointer"
                            title="Center element horizontally on paper"
                          >
                            <span>⇹ Center H</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleAlignPageCenterV}
                            className="flex items-center justify-center gap-1 px-2 py-1 bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-medium transition cursor-pointer"
                            title="Center element vertically on paper"
                          >
                            <span>⇳ Center V</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleAlignLeftMargin}
                            className="flex items-center justify-center gap-1 px-2 py-1 bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-medium transition cursor-pointer"
                            title={`Snap to left printable margin (${paperPadding}mm)`}
                          >
                            <span>⇤ Left Margin</span>
                          </button>
                          <button
                            type="button"
                            onClick={handleAlignRightMargin}
                            className="flex items-center justify-center gap-1 px-2 py-1 bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-medium transition cursor-pointer"
                            title={`Snap to right printable margin (${paperPadding}mm)`}
                          >
                            <span>Right Margin ⇥</span>
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={handleFitPageWidth}
                          className="w-full flex items-center justify-center gap-1 px-2 py-1 bg-white hover:bg-amber-50 text-slate-700 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-medium transition cursor-pointer"
                          title="Stretch element across printable width (left to right margin)"
                        >
                          <span>⬌ Fit Printable Width ({Number((paperWidth - (paperPadding * 2)).toFixed(1))} mm)</span>
                        </button>
                      </div>

                      {/* Content / Text Alignment (if text or table) */}
                      {activeTelemetry.type !== "line" && (
                        <div className="pt-1 border-t border-slate-200 space-y-1.5">
                          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider block">
                            Text &amp; Content Alignment
                          </span>
                          <div className="grid grid-cols-3 gap-1">
                            <button
                              type="button"
                              onClick={() => handleUpdateSelectedSchema({ alignment: "left" })}
                              className={`flex items-center justify-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border transition cursor-pointer ${
                                activeTelemetry.alignment === "left"
                                  ? "bg-amber-500 text-white border-amber-600 font-bold shadow-2xs"
                                  : "bg-white hover:bg-amber-50 text-slate-700 border-slate-200"
                              }`}
                              title="Align content left"
                            >
                              <FiAlignLeft className="w-3 h-3" />
                              <span>Left</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateSelectedSchema({ alignment: "center" })}
                              className={`flex items-center justify-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border transition cursor-pointer ${
                                activeTelemetry.alignment === "center"
                                  ? "bg-amber-500 text-white border-amber-600 font-bold shadow-2xs"
                                  : "bg-white hover:bg-amber-50 text-slate-700 border-slate-200"
                              }`}
                              title="Align content center"
                            >
                              <FiAlignCenter className="w-3 h-3" />
                              <span>Center</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateSelectedSchema({ alignment: "right" })}
                              className={`flex items-center justify-center gap-1 px-2 py-1 rounded text-[10.5px] font-medium border transition cursor-pointer ${
                                activeTelemetry.alignment === "right"
                                  ? "bg-amber-500 text-white border-amber-600 font-bold shadow-2xs"
                                  : "bg-white hover:bg-amber-50 text-slate-700 border-slate-200"
                              }`}
                              title="Align content right"
                            >
                              <FiAlignRight className="w-3 h-3" />
                              <span>Right</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Micro-Nudge D-Pad Controller */}
                      <div className="pt-1 border-t border-slate-200 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1">
                            <FiMove className="w-3 h-3 text-amber-600" />
                            <span>Micro-Nudge D-Pad</span>
                          </span>
                          {/* Step selector chips */}
                          <div className="flex items-center gap-0.5">
                            {[0.1, 0.5, 1.0, 2.0].map((stepVal) => (
                              <button
                                key={stepVal}
                                type="button"
                                onClick={() => setNudgeStep(stepVal)}
                                className={`px-1.5 py-0.2 rounded text-[9.5px] font-mono font-bold transition cursor-pointer border ${
                                  nudgeStep === stepVal
                                    ? "bg-amber-600 text-white border-amber-600 shadow-2xs"
                                    : "bg-white hover:bg-slate-100 text-slate-600 border-slate-200"
                                }`}
                              >
                                {stepVal}mm
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Visual D-Pad */}
                        <div className="flex flex-col items-center justify-center py-1.5 bg-white rounded border border-slate-200">
                          {/* UP */}
                          <button
                            type="button"
                            onClick={() => handleNudge(0, -nudgeStep)}
                            className="p-1.5 rounded hover:bg-amber-100 text-slate-700 hover:text-amber-800 transition active:scale-95 cursor-pointer border border-transparent hover:border-amber-300"
                            title={`Nudge Up by ${nudgeStep}mm`}
                          >
                            <FiArrowUp className="w-3.5 h-3.5" />
                          </button>
                          {/* LEFT / CENTER / RIGHT */}
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleNudge(-nudgeStep, 0)}
                              className="p-1.5 rounded hover:bg-amber-100 text-slate-700 hover:text-amber-800 transition active:scale-95 cursor-pointer border border-transparent hover:border-amber-300"
                              title={`Nudge Left by ${nudgeStep}mm`}
                            >
                              <FiArrowLeft className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-[9.5px] font-mono text-slate-400 font-semibold px-1">
                              ±{nudgeStep}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleNudge(nudgeStep, 0)}
                              className="p-1.5 rounded hover:bg-amber-100 text-slate-700 hover:text-amber-800 transition active:scale-95 cursor-pointer border border-transparent hover:border-amber-300"
                              title={`Nudge Right by ${nudgeStep}mm`}
                            >
                              <FiArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {/* DOWN */}
                          <button
                            type="button"
                            onClick={() => handleNudge(0, nudgeStep)}
                            className="p-1.5 rounded hover:bg-amber-100 text-slate-700 hover:text-amber-800 transition active:scale-95 cursor-pointer border border-transparent hover:border-amber-300"
                            title={`Nudge Down by ${nudgeStep}mm`}
                          >
                            <FiArrowDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* PHASE 2: Magnetic Grid Snapping Card */}
                <div className="bg-slate-50 border border-slate-200 rounded p-2.5 space-y-2">
                  <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                    <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider flex items-center gap-1" style={INTER}>
                      <FiGrid className="w-3.5 h-3.5 text-amber-600" />
                      <span>Magnetic Grid Snapping</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setSnapGridEnabled((prev) => !prev)}
                      className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono transition cursor-pointer border ${
                        snapGridEnabled
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-2xs"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      {snapGridEnabled ? "GRID ON" : "GRID OFF"}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 font-medium">Grid Spacing:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 5, 10].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => {
                            setGridSize(val);
                            setSnapGridEnabled(true);
                          }}
                          className={`px-1.5 py-0.5 rounded text-[9.5px] font-mono font-bold transition cursor-pointer border ${
                            gridSize === val && snapGridEnabled
                              ? "bg-amber-600 text-white border-amber-600 shadow-2xs"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                          }`}
                        >
                          {val}mm
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleSnapToGrid(gridSize)}
                    className="w-full flex items-center justify-center gap-1.5 px-2 py-1 bg-white hover:bg-amber-50 text-slate-800 hover:text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10.5px] font-semibold transition cursor-pointer"
                    title={`Snap selected element(s) to nearest ${gridSize}mm grid coordinate`}
                  >
                    <span>🧲 Snap Selected to {gridSize}mm Grid</span>
                  </button>
                </div>

                {/* Visual Alignment Overlay Grid (Rendered directly on canvas sheet) */}
                <div className="bg-slate-50 border border-slate-200 rounded p-2.5 space-y-2">
                  <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                    <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider flex items-center gap-1" style={INTER}>
                      <FiEye className="w-3.5 h-3.5 text-blue-600" />
                      <span>Faint Canvas Overlay</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowCanvasOverlayGrid((prev) => !prev)}
                      className={`px-2 py-0.5 rounded text-[10px] font-bold font-mono transition cursor-pointer border ${
                        showCanvasOverlayGrid
                          ? "bg-blue-600 text-white border-blue-600 shadow-2xs"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      {showCanvasOverlayGrid ? "GRID VISIBLE" : "GRID HIDDEN"}
                    </button>
                  </div>

                  {/* Grid Density */}
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-500 font-medium">Grid Density:</span>
                    <div className="flex items-center gap-1">
                      {[1, 2, 5, 10].map((val) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => {
                            setOverlayGridSpacing(val);
                            setShowCanvasOverlayGrid(true);
                          }}
                          className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold transition cursor-pointer border ${
                            overlayGridSpacing === val && showCanvasOverlayGrid
                              ? "bg-blue-600 text-white border-blue-600 shadow-2xs"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                          }`}
                        >
                          {val}mm
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Guide lines toggles */}
                  <div className="grid grid-cols-2 gap-1.5 pt-1 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => setShowCenterCrosshairs((prev) => !prev)}
                      className={`px-2 py-1 rounded text-[10px] font-semibold border transition cursor-pointer text-center ${
                        showCenterCrosshairs
                          ? "bg-red-50 text-red-700 border-red-300 font-bold"
                          : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {showCenterCrosshairs ? "✓ Center Crosshair" : "+ Center Crosshair"}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowMarginBounds((prev) => !prev)}
                      className={`px-2 py-1 rounded text-[10px] font-semibold border transition cursor-pointer text-center ${
                        showMarginBounds
                          ? "bg-amber-50 text-amber-800 border-amber-300 font-bold"
                          : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {showMarginBounds ? "✓ Margin Safe-Zone" : "+ Margin Safe-Zone"}
                    </button>
                  </div>
                </div>

                {/* Canvas Zoom & Viewport Inspection Suite */}
                <div className="bg-slate-50 border border-slate-200 rounded p-2.5 space-y-2">
                  <div className="flex items-center justify-between pb-1 border-b border-slate-200">
                    <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider flex items-center gap-1" style={INTER}>
                      <FiMaximize2 className="w-3.5 h-3.5 text-amber-600" />
                      <span>Canvas Zoom & Close-Up</span>
                    </span>
                    <span className="px-1.5 py-0.2 rounded bg-amber-500 text-white font-mono font-bold text-[10px] shadow-2xs">
                      {zoomLevel}
                    </span>
                  </div>

                  {/* Preset Quick Chips */}
                  <div className="space-y-1">
                    <span className="text-[10px] text-slate-500 font-medium block">Zoom Presets:</span>
                    <div className="grid grid-cols-5 gap-1">
                      {ZOOM_LEVELS.map((lvl) => {
                        const lvlStr = `${lvl}%`;
                        const isActive = zoomLevel === lvlStr;
                        return (
                          <button
                            key={lvl}
                            type="button"
                            onClick={() => setZoomLevel(lvlStr)}
                            className={`px-1 py-0.5 rounded text-[9.5px] font-mono transition cursor-pointer border text-center ${
                              isActive
                                ? "bg-amber-600 text-white border-amber-600 font-bold shadow-2xs"
                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
                            }`}
                          >
                            {lvlStr}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stepper Buttons */}
                  <div className="flex items-center gap-1.5 pt-1 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={handleZoomOut}
                      disabled={parseFloat(zoomLevel) <= 25}
                      className="flex-1 flex items-center justify-center gap-1 py-1 bg-white hover:bg-slate-100 disabled:opacity-40 text-slate-700 border border-slate-200 rounded text-[10px] font-semibold transition cursor-pointer"
                      title="Zoom Out (Ctrl + -)"
                    >
                      <FiMinus className="w-3 h-3" />
                      <span>Zoom -</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleZoomReset}
                      className="px-2 py-1 bg-white hover:bg-amber-50 text-amber-900 border border-slate-200 hover:border-amber-300 rounded text-[10px] font-bold transition cursor-pointer"
                      title="Reset Zoom to 100% (Ctrl + 0)"
                    >
                      1:1 (100%)
                    </button>
                    <button
                      type="button"
                      onClick={handleZoomIn}
                      disabled={parseFloat(zoomLevel) >= 400}
                      className="flex-1 flex items-center justify-center gap-1 py-1 bg-white hover:bg-slate-100 disabled:opacity-40 text-slate-700 border border-slate-200 rounded text-[10px] font-semibold transition cursor-pointer"
                      title="Zoom In (Ctrl + +)"
                    >
                      <FiPlus className="w-3 h-3" />
                      <span>Zoom +</span>
                    </button>
                  </div>
                </div>

                {/* Direct Canvas Editing Instruction */}
                <div className="p-2.5 bg-amber-50/70 border border-amber-200 rounded text-amber-900 space-y-1">
                  <div className="flex items-center gap-1.5 font-bold text-xs">
                    <FiSliders className="w-3.5 h-3.5 text-amber-700" />
                    <span>Canvas Visual Controls</span>
                  </div>
                  <p className="text-[10.5px] leading-relaxed text-amber-800">
                    Click any element on canvas to reveal live resize handles, font size selectors, color pickers, and alignment controls.
                  </p>
                </div>

                {/* Keyboard Shortcuts Cheatsheet */}
                <div className="border border-slate-200 rounded p-2.5 space-y-1.5 bg-white">
                  <span className="font-bold text-slate-700 text-[10.5px] uppercase tracking-wider block" style={INTER}>
                    Studio Shortcuts
                  </span>
                  <div className="space-y-1 text-[11px] text-slate-600">
                    <div className="flex justify-between py-0.5 border-b border-slate-100">
                      <span>Save Template:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Ctrl+S</kbd>
                    </div>
                    <div className="flex justify-between py-0.5 border-b border-slate-100">
                      <span>Zoom In / Out:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Ctrl + / -</kbd>
                    </div>
                    <div className="flex justify-between py-0.5 border-b border-slate-100">
                      <span>Reset Zoom:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Ctrl + 0</kbd>
                    </div>
                    <div className="flex justify-between py-0.5 border-b border-slate-100">
                      <span>Delete Element:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Backspace / Del</kbd>
                    </div>
                    <div className="flex justify-between py-0.5 border-b border-slate-100">
                      <span>Nudge Position:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Arrow Keys</kbd>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span>Multi-select:</span>
                      <kbd className="bg-slate-100 border border-slate-300 px-1 rounded font-mono text-[10px]">Shift+Click</kbd>
                    </div>
                  </div>
                </div>

              </div>
            )}

          </aside>

        </div>

      </div>

      {/* ── 4. Live Test Render PDF Modal ─────────────────────────────────────── */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 pt-14 pb-6 sm:p-5 sm:pt-14 sm:pb-8 animate-in fade-in zoom-in-95 duration-150">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[78vh] max-h-[calc(100vh-120px)] flex flex-col overflow-hidden border border-slate-200">
            
            {/* Modal Header */}
            <div className="px-4 py-2 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <FiEye className="w-4 h-4 text-sky-400" />
                <span className="font-bold text-xs sm:text-sm tracking-tight" style={INTER}>
                  Live Test Render — {previewTitle || `${templateName} (${selectedVoucherType})`}
                </span>
                <span className="text-[10px] bg-sky-500/20 text-sky-300 border border-sky-400/30 px-1.5 py-0.2 rounded font-mono">
                  {previewDimensions || `${paperWidth} × ${paperHeight} mm`}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowPreviewModal(false);
                  if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
                  setPreviewPdfUrl(null);
                }}
                className="w-6 h-6 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body: Embedded PDF or Loading spinner */}
            <div className="flex-1 bg-slate-100 flex items-center justify-center overflow-hidden relative min-h-0">
              {isPreviewLoading ? (
                <div className="flex flex-col items-center gap-2 text-slate-500">
                  <FiRefreshCw className="w-6 h-6 animate-spin text-sky-600" />
                  <span className="text-xs font-semibold">Compiling dynamic PDF using @pdfme/generator...</span>
                </div>
              ) : previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  title="PDF Preview"
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="text-xs text-slate-400">Failed to load preview stream.</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0 text-xs">
              <div className="flex items-center gap-1.5 text-slate-600 truncate">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                <span className="truncate">
                  {selectedPaperId === "58MM" || selectedPaperId === "80MM" || paperWidth <= 80 ? (
                    <span className="text-amber-800 font-medium">
                      💡 Thermal POS Tip: In print dialog, set <strong className="font-bold text-slate-900">Margins: None</strong> and <strong className="font-bold text-slate-900">Scale: Actual size (100%)</strong>.
                    </span>
                  ) : (
                    <span>Simulated test inputs populated from <code className="font-mono text-slate-700">systemTokens.js</code></span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {previewPdfUrl && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const iframe = document.querySelector('iframe[title="PDF Preview"]');
                        if (iframe && iframe.contentWindow) {
                          iframe.contentWindow.focus();
                          iframe.contentWindow.print();
                        } else {
                          const printWin = window.open(previewPdfUrl);
                          if (printWin) {
                            printWin.focus();
                            printWin.print();
                          }
                        }
                      }}
                      className="flex items-center gap-1 px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-500 rounded font-semibold transition cursor-pointer shadow-xs"
                      title="Direct Print Preview Document"
                    >
                      <FiPrinter className="w-3.5 h-3.5" />
                      Print
                    </button>
                    <a
                      href={previewPdfUrl}
                      download={`${templateName.replace(/\s+/g, "_")}_preview.pdf`}
                      className="flex items-center gap-1 px-3 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-300 rounded font-semibold transition"
                    >
                      <FiDownload className="w-3.5 h-3.5" />
                      Download PDF
                    </a>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setShowPreviewModal(false)}
                  className="px-3 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded font-medium transition cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* ── 5. Jewellery Preset & Template Gallery Modal (AppConfigMaster Pattern) ── */}
      {showPresetGallery && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/70 backdrop-blur-xs flex items-center justify-center p-3 pt-14 pb-6 sm:p-5 sm:pt-14 sm:pb-8 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[80vh] max-h-[calc(100vh-120px)] flex flex-col overflow-hidden border border-slate-200">
            
            {/* Modal Header: Royal Navy Gradient */}
            <div className="px-4 py-2 bg-gradient-to-r from-[#0f2d59] via-[#1a3a6b] to-[#0f2d59] text-white flex items-center justify-between shrink-0 border-b border-amber-500/30">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center font-bold text-[11px] shadow-xs shrink-0">
                  ★
                </div>
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <h3 className="font-bold text-xs sm:text-sm tracking-tight text-white whitespace-nowrap" style={INTER}>
                    Jewellery Print Preset Gallery
                  </h3>
                  <span className="text-[9px] bg-amber-400/20 text-amber-300 border border-amber-400/40 px-1.5 py-0.2 rounded-full font-semibold shrink-0">
                    {PRESET_GALLERY_ITEMS.length} Production Layouts
                  </span>
                  <span className="text-slate-400 text-xs hidden md:inline">•</span>
                  <p className="text-[10px] text-slate-300 hidden md:inline truncate">
                    Select any pre-configured template to load directly into the designer canvas.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowPresetGallery(false)}
                className="w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10 transition cursor-pointer shrink-0"
                title="Close Preset Gallery"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Filter and Search Bar */}
            <div className="px-4 py-1.5 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0">
              {/* Category Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {[
                  { id: "all", label: "✨ All Presets", count: PRESET_GALLERY_ITEMS.length },
                  { id: "sales", label: "💎 Sales Billing", count: PRESET_GALLERY_ITEMS.filter(p => p.category === "sales").length },
                  { id: "purchase", label: "⚖️ Purchase & Old Gold", count: PRESET_GALLERY_ITEMS.filter(p => p.category === "purchase").length },
                  { id: "vouchers", label: "📜 Receipt & Payment", count: PRESET_GALLERY_ITEMS.filter(p => p.category === "vouchers").length },
                  { id: "tags", label: "🏷️ Tags & Barcodes", count: PRESET_GALLERY_ITEMS.filter(p => p.category === "tags").length }
                ].map((tab) => {
                  const active = presetGalleryCategory === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setPresetGalleryCategory(tab.id)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition flex items-center gap-1.5 cursor-pointer select-none ${
                        active
                          ? "bg-[#0f2d59] text-white shadow-xs"
                          : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-100"
                      }`}
                    >
                      <span>{tab.label}</span>
                      <span className={`text-[9.5px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Quick Search Box */}
              <div className="relative min-w-[200px]">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  value={presetSearch}
                  onChange={(e) => setPresetSearch(e.target.value)}
                  placeholder="Search preset or paper..."
                  className="w-full pl-8 pr-3 py-1 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30"
                />
                {presetSearch && (
                  <button
                    type="button"
                    onClick={() => setPresetSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <FiX className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Gallery Card Grid */}
            <div className="flex-1 overflow-y-auto p-4 studio-scrollbar bg-slate-100/70 min-h-0">
              {filteredPresets.length === 0 ? (
                <div className="p-10 text-center text-slate-400 flex flex-col items-center justify-center">
                  <FiLayout className="w-8 h-8 text-slate-300 mb-2" />
                  <p className="font-semibold text-sm">No templates found matching your search</p>
                  <p className="text-xs text-slate-400 mt-1">Try resetting the category filter or searching for another keyword.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filteredPresets.map((item) => {
                    const isActive = templateName === item.name;
                    return (
                      <div
                        key={item.id}
                        className={`group relative flex flex-col rounded-xl border transition-all duration-200 bg-white overflow-hidden shadow-xs ${
                          isActive
                            ? "border-amber-600 ring-2 ring-amber-500/25 shadow-md transform -translate-y-0.5"
                            : "border-slate-200/90 hover:border-amber-500/70 hover:shadow-md hover:-translate-y-0.5"
                        }`}
                      >
                        {/* Miniature Card Header Canvas (AppConfigMaster pattern) */}
                        <div
                          className="h-[80px] w-full relative overflow-hidden flex items-center justify-between p-2.5 select-none"
                          style={{ background: item.cardBg }}
                        >
                          {/* 1. Concentric watermark silk rings */}
                          <div className={`absolute -top-7 -right-7 w-20 h-20 rounded-full border ${item.ringColor} pointer-events-none opacity-60`} />
                          <div className={`absolute -top-3 -right-3 w-12 h-12 rounded-full border ${item.ringColor} pointer-events-none opacity-60`} />

                          {/* Quick Hover Preview Button */}
                          <button
                            type="button"
                            onClick={() => handlePreviewPreset(item)}
                            className="opacity-0 group-hover:opacity-100 transition absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-black/60 hover:bg-black/80 text-white rounded text-[8.5px] font-semibold flex items-center gap-1 shadow-xs cursor-pointer z-20 backdrop-blur-xs"
                            title="Instant PDF Preview"
                          >
                            <FiEye className="w-2.5 h-2.5 text-sky-300" />
                            <span>Preview</span>
                          </button>

                          {/* Left: Medallion & Category Badge */}
                          <div className="relative z-10 flex flex-col justify-between h-full max-w-[55%]">
                            <div className="flex items-center gap-1">
                              <span className={`text-[8px] font-bold px-1.5 py-0.2 rounded-full border ${item.themeBadge} uppercase tracking-wider backdrop-blur-xs`}>
                                {item.categoryLabel}
                              </span>
                            </div>
                            <div>
                              <span className="text-[10.5px] font-bold text-white block leading-tight drop-shadow-xs">
                                {item.paperSize}
                              </span>
                              <span className="text-[8.5px] font-mono text-slate-300">
                                {item.dimensions}
                              </span>
                            </div>
                          </div>

                          {/* Right: Paper Mockup Silhouette */}
                          <div className="relative z-10 flex items-center justify-center">
                            <PresetCardThumbnail item={item} />
                          </div>
                        </div>

                        {/* Card Content & Features */}
                        <div className="p-2.5 flex-1 flex flex-col justify-between">
                          <div>
                            {/* Title */}
                            <h4 className="font-bold text-[11.5px] text-slate-900 leading-snug group-hover:text-amber-800 transition line-clamp-1 mb-1" style={INTER}>
                              {item.name}
                            </h4>

                            <p className="text-[9.5px] text-slate-500 leading-snug line-clamp-2 mb-2">
                              {item.description}
                            </p>

                            {/* Highlight Feature Badges */}
                            <div className="flex flex-wrap gap-1 mb-2">
                              {item.highlights.slice(0, 3).map((h, i) => (
                                <span
                                  key={i}
                                  className="text-[8px] font-semibold bg-slate-100 text-slate-600 px-1 py-0.2 rounded border border-slate-200/80"
                                >
                                  ✓ {h}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-1">
                            <span className="text-[8.5px] font-mono text-slate-400 truncate max-w-[65px]">
                              {item.columnsCount}
                            </span>

                            <div className="flex items-center gap-1 shrink-0">
                              {/* Preview Action Button */}
                              <button
                                type="button"
                                onClick={() => handlePreviewPreset(item)}
                                className="flex items-center gap-1 px-2 py-1 rounded-md bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 font-bold text-[10px] transition active:scale-95 cursor-pointer shadow-2xs"
                                title={`Preview rendered PDF for ${item.name}`}
                              >
                                <FiEye className="w-3 h-3 text-sky-600" />
                                <span>Preview</span>
                              </button>

                              {isActive ? (
                                <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                                  <FiCheck className="w-3 h-3 text-emerald-600" />
                                  Active
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleLoadPreset(item)}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-bold text-[10px] shadow-2xs transition active:scale-95 cursor-pointer"
                                  title={`Load ${item.name} into designer canvas`}
                                >
                                  <span>Load</span>
                                  <FiChevronRight className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>

                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-2 bg-white border-t border-slate-200 flex items-center justify-between shrink-0 text-[11px] text-slate-500">
              <div className="flex items-center gap-1.5 truncate mr-2">
                <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span className="truncate">Includes realistic dummy data, dynamic system tokens, &amp; BIS Hallmark compliance.</span>
              </div>
              <button
                type="button"
                onClick={() => setShowPresetGallery(false)}
                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition cursor-pointer shrink-0"
              >
                Close Gallery
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 🖨️ PRINT MODEL ASSIGNER & VOUCHER ROUTING MODAL                      */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 🖨️ PRINT MODEL ASSIGNER & VOUCHER ROUTING MODAL                      */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      {showAssignerModal && (
        <PrintModelAssigner
          onClose={() => {
            setShowAssignerModal(false);
            fetchAssignments();
          }}
          zIndex={(zIndex || 101) + 2}
          onOpenDesigner={(tplId) => {
            setShowAssignerModal(false);
            if (tplId) {
              const match = savedTemplates.find((t) => Number(t.id) === Number(tplId) || t.id === tplId);
              if (match) {
                handleSelectTemplate(match);
              }
            }
          }}
        />
      )}

      {/* ────────────────────────────────────────────────────────────────────── */}
      {/* 📖 INTERACTIVE USER MANUAL & COMPONENT GUIDE MODAL                   */}
      {/* ────────────────────────────────────────────────────────────────────── */}
      <InvoiceDesignerManualModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        zIndex={(zIndex || 101) + 50}
      />

    </div>
  );
}